// @ts-nocheck
/* Procedural audio engine ported verbatim from Hen House v0.9 (js/audio.js).
   5 SFX themes × 16 sounds, per-breed clucks/crows, 15 generative music tracks.
   Only change: localStorage pref keys use the v1 prefix. Typed facade: src/platform/audio.ts */
/**
 * Hen House — Audio Module (P3 Hybrid Synth+Sample Engine)
 *
 * Architecture:
 *   audioCtx → masterGain → compressor → destination
 *   ├── sfxBus (GainNode) — player-controlled SFX volume
 *   ├── ambientBus (GainNode) — player-controlled ambient volume
 *   └── musicBus (GainNode) — reserved for future
 *
 * Features:
 *   - Switchable sound themes (pastoral/arcade) persisted to localStorage
 *   - Procedural synth engine (all sounds generated, zero external assets)
 *   - 3-axis variation: pitch ±10%, volume ±15%, timing ±20ms
 *   - Rate limiter: 100ms cooldown per sound ID
 *   - Frame limiter: max 3 sounds per ~16ms frame
 *   - Visibility API: fade ambient/music on tab hide
 *   - Audio OFF by default (idle game convention)
 */

// ── Audio graph nodes ───────────────────────────────────────────────
let audioCtx = null;
let masterGain = null;
let compressor = null;
let sfxBus = null;
let ambientBus = null;
let musicBus = null;

// ── State ───────────────────────────────────────────────────────────
let enabled = false;
let loaded = false;
// v1.5.3 §2 — `currentTheme` drives SFX synth lookup in SOUND_THEMES; the
// background music selector is now independent (`currentMusic`) so players
// can pair any SFX theme with any music bed. Prior to v1.5.3 both were
// keyed off `currentTheme`, which meant picking "arcade" sound also
// swapped the ambient bed.
let currentTheme = 'pastoral';
let currentMusic = 'pastoral';
// v1.5.4 §1 — background-music mode: 'random' (default) auto-picks a track
// on enable and re-shuffles every SHUFFLE_INTERVAL_MS. 'specific' pins to
// a track the player chose in settings. The mode is persisted separately
// from the track key so a player who sets "specific: harvest_moon" keeps
// that across sessions rather than getting re-shuffled next load.
let _musicMode = 'random';
let _shuffleTimer = null;
const SHUFFLE_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
let _masterVol = 0.7;
let _sfxVol = 0.8;
let _ambientVol = 0.5;

// Ambient loop state
let _ambientStopFn = null;   // cleanup function for current ambient loop
let _ambientFadeGain = null; // GainNode for crossfade control

// Rate limiting: last play time per sound ID (ms, from performance.now)
const lastPlayTime = {};
const RATE_LIMIT_MS = 100;

// Frame limiting: cap concurrent sounds per ~16ms frame
let _frameSoundCount = 0;
let _frameTime = 0;
const MAX_SOUNDS_PER_FRAME = 3;

// Sample buffers keyed by "${theme}_${soundId}" (reserved for future CC0 samples)
const sampleBuffers = {};

// Sound priority (higher = more important, used for frame limiting)
const SOUND_PRIORITY = {
  milestone: 10,
  matriarch_promote: 9,
  yolk_burst: 8,
  egg_shower: 8,
  double_time: 8,
  golden_egg: 8,
  rooster_crow: 7,
  rotten_egg: 6,
  broody_coo: 5,
  purchase: 4,
  butterfly_click: 4,
  bonus_click: 3,
  worm_click: 3,
  hen_cluck: 2,
  rooster_cluck: 2,
  egg_click: 1
};

// Visibility API: stored volumes for restore after tab show
let _ambientBeforeHide = 0;
let _musicBeforeHide = 0;

// ── Crow Patches (v1.2) ─────────────────────────────────────────────
// Per-breed synth modifiers applied to the base `rooster_crow` generator.
// Applied via `audioManager.playCrow(breedId)` — the patch is packed into
// the variation object so theme fns can read it.
//
// Axes:
//   pitch     — multiplier on all oscillator fundamentals (0.6 deep → 1.5 high)
//   bright    — multiplier on formant/harmonic amplitude (0.75 muted → 1.4 brassy)
//   env       — multiplier on attack+sustain+decay durations (0.7 staccato → 1.4 stretched)
//   waveform  — primary oscillator type ('triangle' default; 'sine' soft, 'sawtooth' buzzy, 'square' sharp)
//   syllables — 1..3 — how many "doo" tails to append
//   vol       — overall volume multiplier (0.85..1.2)
//
// v1.4 Option C Polish+: all 5 themes (pastoral/arcade/8bit/lofi/spooky) now
// read all 6 axes. Prior to v1.4, arcade/8bit/lofi/spooky only honored pitch
// + vol via the variation pipeline; now env (duration), bright (amplitude),
// waveform, and syllables (repeat count) all shape the theme-specific synth.
// All 31 v1.2 breeds are represented; missing breeds fall back to DEFAULT
// which mirrors RIR.
// Per-breed hen cluck patches — parallel to CROW_PATCHES but tuned for
// hens (higher fundamentals, sine-leaning waveforms, more syllable variety
// because hens are chattier than roosters). Applied via
// `audioManager.playHenCluck(breedId)`. All 31 v1.2 breeds represented;
// unknown breeds fall back to DEFAULT (RIR).
//
// Axes (same shape as CROW_PATCHES so applyCrowPatch can be reused):
//   pitch     — multiplier on all oscillator fundamentals
//   bright    — multiplier on formant amplitude
//   env       — multiplier on syllable gap + duration
//   waveform  — 'sine' default; kept here for future theme support
//   syllables — base syllable count bias (1–4)
//   vol       — overall volume multiplier
export const HEN_PATCHES = {
  DEFAULT:             { pitch: 1.00, bright: 1.00, env: 1.00, waveform: 'sine', syllables: 2, vol: 1.00 },

  // STARTER — familiar, mid-range
  RIR:                 { pitch: 1.00, bright: 1.00, env: 1.00, waveform: 'sine', syllables: 2, vol: 1.00 },
  LEGHORN:             { pitch: 1.15, bright: 1.20, env: 0.85, waveform: 'sine', syllables: 3, vol: 1.10 }, // chatty
  BUFF_ORPINGTON:      { pitch: 0.85, bright: 0.85, env: 1.20, waveform: 'sine', syllables: 1, vol: 0.90 }, // mellow
  WYANDOTTE:           { pitch: 1.00, bright: 1.05, env: 1.00, waveform: 'sine', syllables: 2, vol: 1.00 },

  // EARLY
  AUSTRALORP:          { pitch: 0.92, bright: 1.00, env: 1.10, waveform: 'sine', syllables: 2, vol: 1.00 },
  PLYMOUTH_ROCK:       { pitch: 0.98, bright: 1.00, env: 1.15, waveform: 'sine', syllables: 2, vol: 1.00 },
  ISA_BROWN:           { pitch: 1.05, bright: 1.00, env: 0.95, waveform: 'sine', syllables: 2, vol: 1.00 },
  WELSUMMER:           { pitch: 0.95, bright: 1.05, env: 1.10, waveform: 'sine', syllables: 2, vol: 1.05 },
  NEW_HAMPSHIRE_RED:   { pitch: 1.05, bright: 1.05, env: 0.95, waveform: 'sine', syllables: 2, vol: 1.05 },

  // MID
  AMERAUCANA:          { pitch: 1.05, bright: 0.90, env: 1.05, waveform: 'sine', syllables: 1, vol: 0.95 }, // soft
  BLACK_COPPER_MARANS: { pitch: 0.88, bright: 1.10, env: 0.90, waveform: 'sine', syllables: 2, vol: 1.05 },
  EASTER_EGGER:        { pitch: 1.10, bright: 1.15, env: 1.00, waveform: 'sine', syllables: 3, vol: 1.05 }, // chatty
  CREAM_LEGBAR:        { pitch: 1.08, bright: 1.05, env: 0.95, waveform: 'sine', syllables: 2, vol: 1.00 },
  BARNEVELDER:         { pitch: 0.95, bright: 1.00, env: 1.05, waveform: 'sine', syllables: 2, vol: 1.00 },

  // LATE
  SILKIE:              { pitch: 0.92, bright: 0.70, env: 1.30, waveform: 'sine', syllables: 1, vol: 0.80 }, // whisper-soft
  BIELEFELDER:         { pitch: 0.82, bright: 1.00, env: 1.10, waveform: 'sine', syllables: 2, vol: 1.10 },
  SVART_HONA:          { pitch: 0.88, bright: 0.85, env: 1.15, waveform: 'sine', syllables: 1, vol: 0.95 },
  OLIVE_EGGER:         { pitch: 0.96, bright: 0.95, env: 1.05, waveform: 'sine', syllables: 2, vol: 1.00 },
  SPECKLED_SUSSEX:     { pitch: 1.00, bright: 1.00, env: 1.05, waveform: 'sine', syllables: 3, vol: 1.05 }, // chatty

  // RARE
  AYAM_CEMANI:         { pitch: 0.78, bright: 1.20, env: 0.95, waveform: 'sine', syllables: 1, vol: 1.10 }, // sinister
  POLISH:              { pitch: 1.18, bright: 1.20, env: 0.85, waveform: 'sine', syllables: 3, vol: 1.00 }, // flustered
  FRIZZLE:             { pitch: 1.12, bright: 1.25, env: 0.90, waveform: 'sine', syllables: 3, vol: 0.95 },
  TURKEN:              { pitch: 0.92, bright: 1.05, env: 1.00, waveform: 'sine', syllables: 2, vol: 1.05 },
  SULTAN:              { pitch: 0.98, bright: 0.80, env: 1.30, waveform: 'sine', syllables: 1, vol: 0.90 },
  DONG_TAO:            { pitch: 0.72, bright: 0.95, env: 1.20, waveform: 'sine', syllables: 1, vol: 1.10 }, // low + slow
  BRAHMA:              { pitch: 0.68, bright: 0.85, env: 1.30, waveform: 'sine', syllables: 1, vol: 1.15 }, // deepest
  COCHIN:              { pitch: 0.82, bright: 0.85, env: 1.25, waveform: 'sine', syllables: 1, vol: 0.95 },
  SERAMA:              { pitch: 1.45, bright: 1.30, env: 0.75, waveform: 'sine', syllables: 3, vol: 0.85 }, // tiny + high
  JAPANESE_BANTAM:     { pitch: 1.40, bright: 1.20, env: 0.80, waveform: 'sine', syllables: 2, vol: 0.90 },
  SEBRIGHT:            { pitch: 1.22, bright: 1.10, env: 0.90, waveform: 'sine', syllables: 2, vol: 0.95 },
  MILLE_FLEUR_DUCCLE:  { pitch: 1.15, bright: 1.10, env: 0.95, waveform: 'sine', syllables: 2, vol: 0.95 },
};

export const CROW_PATCHES = {
  DEFAULT:             { pitch: 1.00, bright: 1.00, env: 1.00, waveform: 'triangle', syllables: 1, vol: 1.00 },

  // STARTER — familiar, balanced
  RIR:                 { pitch: 1.00, bright: 1.00, env: 1.00, waveform: 'triangle', syllables: 1, vol: 1.00 },
  LEGHORN:             { pitch: 1.20, bright: 1.20, env: 0.75, waveform: 'triangle', syllables: 2, vol: 1.05 },
  BUFF_ORPINGTON:      { pitch: 0.85, bright: 0.80, env: 1.20, waveform: 'sine',     syllables: 1, vol: 0.95 },
  WYANDOTTE:           { pitch: 1.00, bright: 1.10, env: 1.00, waveform: 'triangle', syllables: 1, vol: 1.00 },

  // EARLY
  AUSTRALORP:          { pitch: 0.90, bright: 1.00, env: 1.10, waveform: 'sawtooth', syllables: 1, vol: 1.10 },
  PLYMOUTH_ROCK:       { pitch: 0.95, bright: 0.95, env: 1.30, waveform: 'triangle', syllables: 2, vol: 1.00 },
  ISA_BROWN:           { pitch: 1.05, bright: 1.00, env: 1.00, waveform: 'triangle', syllables: 1, vol: 1.00 },
  WELSUMMER:           { pitch: 0.95, bright: 1.00, env: 1.15, waveform: 'sawtooth', syllables: 2, vol: 1.05 },
  NEW_HAMPSHIRE_RED:   { pitch: 1.05, bright: 1.05, env: 0.95, waveform: 'triangle', syllables: 1, vol: 1.05 },

  // MID
  AMERAUCANA:          { pitch: 1.05, bright: 0.90, env: 1.05, waveform: 'sine',     syllables: 1, vol: 1.00 },
  BLACK_COPPER_MARANS: { pitch: 0.85, bright: 1.15, env: 0.85, waveform: 'sawtooth', syllables: 1, vol: 1.10 },
  EASTER_EGGER:        { pitch: 1.10, bright: 1.20, env: 1.00, waveform: 'square',   syllables: 2, vol: 1.05 },
  CREAM_LEGBAR:        { pitch: 1.10, bright: 1.05, env: 0.95, waveform: 'triangle', syllables: 1, vol: 1.00 },
  BARNEVELDER:         { pitch: 0.95, bright: 1.00, env: 1.05, waveform: 'triangle', syllables: 1, vol: 1.00 },

  // LATE
  SILKIE:              { pitch: 0.90, bright: 0.75, env: 1.25, waveform: 'sine',     syllables: 1, vol: 0.90 },
  BIELEFELDER:         { pitch: 0.80, bright: 1.05, env: 1.10, waveform: 'sawtooth', syllables: 1, vol: 1.15 },
  SVART_HONA:          { pitch: 0.85, bright: 0.85, env: 1.15, waveform: 'triangle', syllables: 1, vol: 1.00 },
  OLIVE_EGGER:         { pitch: 0.95, bright: 0.95, env: 1.05, waveform: 'triangle', syllables: 1, vol: 1.00 },
  SPECKLED_SUSSEX:     { pitch: 1.00, bright: 0.95, env: 1.10, waveform: 'triangle', syllables: 2, vol: 1.00 },

  // RARE
  AYAM_CEMANI:         { pitch: 0.75, bright: 1.30, env: 0.90, waveform: 'sawtooth', syllables: 1, vol: 1.20 },
  POLISH:              { pitch: 1.15, bright: 1.25, env: 0.80, waveform: 'square',   syllables: 3, vol: 1.05 },
  FRIZZLE:             { pitch: 1.10, bright: 1.30, env: 0.85, waveform: 'square',   syllables: 2, vol: 1.00 },
  TURKEN:              { pitch: 0.90, bright: 1.10, env: 1.00, waveform: 'sawtooth', syllables: 1, vol: 1.10 },
  SULTAN:              { pitch: 0.95, bright: 0.85, env: 1.35, waveform: 'sine',     syllables: 2, vol: 0.95 },
  DONG_TAO:            { pitch: 0.70, bright: 1.00, env: 1.20, waveform: 'triangle', syllables: 1, vol: 1.15 },
  BRAHMA:              { pitch: 0.65, bright: 0.90, env: 1.30, waveform: 'sawtooth', syllables: 1, vol: 1.20 },
  COCHIN:              { pitch: 0.80, bright: 0.85, env: 1.25, waveform: 'sine',     syllables: 1, vol: 1.00 },
  SERAMA:              { pitch: 1.50, bright: 1.35, env: 0.70, waveform: 'square',   syllables: 2, vol: 0.85 },
  JAPANESE_BANTAM:     { pitch: 1.45, bright: 1.25, env: 0.75, waveform: 'triangle', syllables: 2, vol: 0.90 },
  SEBRIGHT:            { pitch: 1.25, bright: 1.15, env: 0.90, waveform: 'triangle', syllables: 2, vol: 0.95 },
  MILLE_FLEUR_DUCCLE:  { pitch: 1.15, bright: 1.10, env: 0.95, waveform: 'triangle', syllables: 1, vol: 0.95 },
};

// ── Variation Engine ────────────────────────────────────────────────

/** Returns randomized variation axes for a single sound play */
function getVariation() {
  return {
    pitchMult: 0.9 + Math.random() * 0.2,    // ±10% (0.9 to 1.1)
    volumeMult: 0.85 + Math.random() * 0.3,   // ±15% (0.85 to 1.15)
    delay: Math.random() * 0.04 - 0.02        // ±20ms (-0.02 to +0.02)
  };
}

/** Apply a crow patch to a variation object. Mutates and returns v. */
function applyCrowPatch(v, patch) {
  if (!patch) return v;
  v.pitchMult *= patch.pitch;
  v.volumeMult *= patch.vol;
  v.crowBright = patch.bright;
  v.crowEnv = patch.env;
  v.crowWaveform = patch.waveform;
  v.crowSyllables = patch.syllables;
  return v;
}

// ── Persistence helpers ─────────────────────────────────────────────

function _loadPref(key, fallback) {
  if (typeof localStorage === 'undefined') return fallback;
  const v = localStorage.getItem(key);
  return v !== null ? v : fallback;
}

function _savePref(key, value) {
  if (typeof localStorage !== 'undefined') {
    localStorage.setItem(key, value);
  }
}

// ══════════════════════════════════════════════════════════════════════
//  SOUND THEME DEFINITIONS
//  Each theme maps sound IDs → generator(ctx, dest, variation)
//  Generators create Web Audio nodes, connect to dest, auto-stop.
// ══════════════════════════════════════════════════════════════════════

const SOUND_THEMES = {

  // ─── PASTORAL — warm, natural, Stardew Valley feel ──────────────
  pastoral: {

    /** Sine sweep 1000→700 Hz + 2nd harmonic, soft attack, 120ms decay */
    egg_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.22 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(dest);

      const o1 = ctx.createOscillator();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(1000 * v.pitchMult, t);
      o1.frequency.exponentialRampToValueAtTime(700 * v.pitchMult, t + 0.12);
      o1.connect(g);
      o1.start(t);
      o1.stop(t + 0.16);

      // 2nd harmonic — softer
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t);
      g2.gain.linearRampToValueAtTime(0.09 * v.volumeMult, t + 0.01);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      g2.connect(dest);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(2000 * v.pitchMult, t);
      o2.frequency.exponentialRampToValueAtTime(1400 * v.pitchMult, t + 0.12);
      o2.connect(g2);
      o2.start(t);
      o2.stop(t + 0.13);
    },

    /** Multi-syllable "buk-buk" cluck — glottal pulse + vocal formant + upper resonance.
     *  v1.4 per-breed: reads v.crowBright/env/syllables from HEN_PATCHES when set
     *  (via playHenCluck). Falls back to prior randomized behavior without a patch. */
    hen_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const bright = v.crowBright || 1.0;
      const envMult = v.crowEnv || 1.0;

      // Per-breed syllable bias when a HEN_PATCH is active, otherwise randomize.
      let syllables;
      if (typeof v.crowSyllables === 'number') {
        // Bias around the patch value: ±1 with slight randomness.
        const r = Math.random();
        const base = v.crowSyllables;
        syllables = r < 0.2 ? Math.max(1, base - 1) : r < 0.85 ? base : Math.min(4, base + 1);
      } else {
        const r = Math.random();
        syllables = r < 0.5 ? 1 : r < 0.85 ? 2 : 3;
      }
      const gap = (0.08 + Math.random() * 0.04) * envMult;

      for (let s = 0; s < syllables; s++) {
        const st = t + s * gap;
        const vol = (s === 0 ? 1.0 : 0.85) * v.volumeMult;

        // Vocal body — sine sweep 380→250 Hz (fundamental)
        const bodyG = ctx.createGain();
        bodyG.gain.setValueAtTime(0.001, st);
        bodyG.gain.linearRampToValueAtTime(0.20 * vol, st + 0.003);
        bodyG.gain.exponentialRampToValueAtTime(0.001, st + 0.12 * envMult);
        bodyG.connect(dest);

        const body = ctx.createOscillator();
        body.type = 'sine';
        body.frequency.setValueAtTime(380 * v.pitchMult, st);
        body.frequency.exponentialRampToValueAtTime(250 * v.pitchMult, st + 0.08 * envMult);
        body.connect(bodyG);
        body.start(st);
        body.stop(st + 0.13 * envMult);

        // Upper formant — resonance around 1800 Hz for realism (scaled by bright)
        const fmtG = ctx.createGain();
        fmtG.gain.setValueAtTime(0.001, st);
        fmtG.gain.linearRampToValueAtTime(0.06 * bright * vol, st + 0.002);
        fmtG.gain.exponentialRampToValueAtTime(0.001, st + 0.06 * envMult);
        fmtG.connect(dest);

        const fmt = ctx.createOscillator();
        fmt.type = 'sine';
        fmt.frequency.setValueAtTime(1800 * v.pitchMult, st);
        fmt.frequency.exponentialRampToValueAtTime(1400 * v.pitchMult, st + 0.05 * envMult);
        fmt.connect(fmtG);
        fmt.start(st);
        fmt.stop(st + 0.07 * envMult);

        // Glottal click — sharp bandpass noise burst
        const nLen = Math.floor(ctx.sampleRate * 0.04);
        const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
        const nD = nBuf.getChannelData(0);
        for (let i = 0; i < nLen; i++) nD[i] = Math.random() * 2 - 1;

        const noise = ctx.createBufferSource();
        noise.buffer = nBuf;

        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.setValueAtTime(1500 * v.pitchMult, st);
        bp.Q.setValueAtTime(6, st);

        const nG = ctx.createGain();
        nG.gain.setValueAtTime(0.001, st);
        nG.gain.linearRampToValueAtTime(0.10 * vol, st + 0.002);
        nG.gain.exponentialRampToValueAtTime(0.001, st + 0.035);

        noise.connect(bp);
        bp.connect(nG);
        nG.connect(dest);
        noise.start(st);
        noise.stop(st + 0.04);
      }
    },

    /**
     * Rooster cluck — deeper/gruffer than hen_cluck. Tidbitting/food-call
     * pattern: a series of guttural "buck-buck-buck" pulses. Uses CROW_PATCHES
     * breed shaping (pitch/bright/env) so each rooster breed clucks at its
     * crow register. Three internal variants rotate per call:
     *   V0 "tidbit"   3 syllables, first-accent — standard food call (50%)
     *   V1 "soft coo" 2 syllables, deeper/quieter — idle content (25%)
     *   V2 "alert"    4 rapid short bursts, higher — startled/excited (25%)
     */
    rooster_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const bright = v.crowBright || 1.0;
      const envMult = v.crowEnv || 1.0;
      const wave = v.crowWaveform || 'sawtooth';

      const r = Math.random();
      let syllables, gap, baseHz, endHz, sylDur, accentFirst, vol;
      if (r < 0.50) { // V0 tidbit
        syllables = 3; gap = 0.13; baseHz = 230; endHz = 150; sylDur = 0.14; accentFirst = true; vol = 1.00;
      } else if (r < 0.75) { // V1 soft coo
        syllables = 2; gap = 0.16; baseHz = 195; endHz = 135; sylDur = 0.17; accentFirst = false; vol = 0.75;
      } else { // V2 alert
        syllables = 4; gap = 0.09; baseHz = 270; endHz = 180; sylDur = 0.09; accentFirst = false; vol = 1.05;
      }
      sylDur *= envMult;

      for (let s = 0; s < syllables; s++) {
        const st = t + s * (gap * envMult);
        const amp = (accentFirst && s === 0 ? 1.0 : (!accentFirst && s === 0 ? 0.9 : 0.78)) * vol * v.volumeMult;

        // Gruff vocal body — sawtooth harmonic for rooster grit
        const bodyG = ctx.createGain();
        bodyG.gain.setValueAtTime(0.001, st);
        bodyG.gain.linearRampToValueAtTime(0.22 * amp, st + 0.004);
        bodyG.gain.exponentialRampToValueAtTime(0.001, st + sylDur);
        bodyG.connect(dest);

        const body = ctx.createOscillator();
        body.type = wave;
        body.frequency.setValueAtTime(baseHz * v.pitchMult, st);
        body.frequency.exponentialRampToValueAtTime(endHz * v.pitchMult, st + sylDur * 0.7);
        body.connect(bodyG);
        body.start(st);
        body.stop(st + sylDur + 0.02);

        // Lower formant (1200 Hz vs hen's 1800) — deeper resonance
        const fmtG = ctx.createGain();
        fmtG.gain.setValueAtTime(0.001, st);
        fmtG.gain.linearRampToValueAtTime(0.05 * bright * amp, st + 0.003);
        fmtG.gain.exponentialRampToValueAtTime(0.001, st + sylDur * 0.6);
        fmtG.connect(dest);

        const fmt = ctx.createOscillator();
        fmt.type = 'sine';
        fmt.frequency.setValueAtTime(1200 * v.pitchMult, st);
        fmt.frequency.exponentialRampToValueAtTime(900 * v.pitchMult, st + sylDur * 0.5);
        fmt.connect(fmtG);
        fmt.start(st);
        fmt.stop(st + sylDur * 0.7);

        // Glottal noise burst — lower-centered bandpass (rooster gruffness)
        const nLen = Math.floor(ctx.sampleRate * 0.05);
        const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
        const nD = nBuf.getChannelData(0);
        for (let i = 0; i < nLen; i++) nD[i] = Math.random() * 2 - 1;

        const noise = ctx.createBufferSource();
        noise.buffer = nBuf;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.setValueAtTime(900 * v.pitchMult, st);
        bp.Q.setValueAtTime(5, st);

        const nG = ctx.createGain();
        nG.gain.setValueAtTime(0.001, st);
        nG.gain.linearRampToValueAtTime(0.12 * amp, st + 0.002);
        nG.gain.exponentialRampToValueAtTime(0.001, st + 0.045);

        noise.connect(bp);
        bp.connect(nG);
        nG.connect(dest);
        noise.start(st);
        noise.stop(st + 0.05);
      }
    },

    /** Multi-phase "cock-a-doodle-doo" — 3 phases with harmonics and vibrato */
    rooster_crow(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      // v1.2 per-breed crow patches. See CROW_PATCHES in audio.js head.
      const env = v.crowEnv || 1.0;         // envelope time-stretch
      const bright = v.crowBright || 1.0;   // formant/harmonic amplitude
      const wave = v.crowWaveform || 'triangle';
      const syllables = Math.max(1, Math.min(3, v.crowSyllables || 1));

      // Phase 1: "cock" — sharp attack, quick rise (0–0.15s * env)
      const p1End = 0.15 * env;
      const g1 = ctx.createGain();
      g1.gain.setValueAtTime(0.001, t);
      g1.gain.linearRampToValueAtTime(0.28 * v.volumeMult, t + 0.02 * env);
      g1.gain.setValueAtTime(0.25 * v.volumeMult, t + 0.10 * env);
      g1.gain.exponentialRampToValueAtTime(0.08 * v.volumeMult, t + p1End);
      g1.connect(dest);

      const o1 = ctx.createOscillator();
      o1.type = wave;
      o1.frequency.setValueAtTime(350 * v.pitchMult, t);
      o1.frequency.exponentialRampToValueAtTime(700 * v.pitchMult, t + 0.12 * env);
      o1.connect(g1);
      o1.start(t);
      o1.stop(t + p1End + 0.01);

      // Phase 2: "a-doodle" — sustained with vibrato
      const p2Start = p1End - 0.01;
      const p2End = 1.0 * env;
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t + p2Start);
      g2.gain.linearRampToValueAtTime(0.30 * v.volumeMult, t + p2Start + 0.06 * env);
      g2.gain.setValueAtTime(0.30 * v.volumeMult, t + p2End - 0.30 * env);
      g2.gain.exponentialRampToValueAtTime(0.10 * v.volumeMult, t + p2End);
      g2.connect(dest);

      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(6, t + p2Start);
      lfo.frequency.linearRampToValueAtTime(8, t + p2End);
      const lfoG = ctx.createGain();
      lfoG.gain.setValueAtTime(25 * v.pitchMult, t + p2Start);
      lfo.connect(lfoG);

      const o2 = ctx.createOscillator();
      o2.type = wave;
      o2.frequency.setValueAtTime(700 * v.pitchMult, t + p2Start);
      o2.frequency.linearRampToValueAtTime(850 * v.pitchMult, t + p2Start + 0.16 * env);
      o2.frequency.setValueAtTime(850 * v.pitchMult, t + p2End - 0.40 * env);
      o2.frequency.linearRampToValueAtTime(750 * v.pitchMult, t + p2End);
      lfoG.connect(o2.frequency);
      o2.connect(g2);
      o2.start(t + p2Start);
      o2.stop(t + p2End + 0.01);
      lfo.start(t + p2Start);
      lfo.stop(t + p2End + 0.01);

      // Nasal formant — resonance around 2200 Hz for realism, scaled by bright
      const fG = ctx.createGain();
      fG.gain.setValueAtTime(0.001, t + p2Start);
      fG.gain.linearRampToValueAtTime(0.08 * v.volumeMult * bright, t + p2Start + 0.08 * env);
      fG.gain.setValueAtTime(0.08 * v.volumeMult * bright, t + p2End - 0.40 * env);
      fG.gain.exponentialRampToValueAtTime(0.001, t + p2End);
      fG.connect(dest);

      const fO = ctx.createOscillator();
      fO.type = 'sine';
      fO.frequency.setValueAtTime(2200 * v.pitchMult, t + p2Start);
      fO.frequency.linearRampToValueAtTime(2400 * v.pitchMult, t + p2Start + 0.16 * env);
      fO.frequency.setValueAtTime(2400 * v.pitchMult, t + p2End - 0.40 * env);
      fO.frequency.exponentialRampToValueAtTime(1800 * v.pitchMult, t + p2End);
      fO.connect(fG);
      fO.start(t + p2Start);
      fO.stop(t + p2End + 0.01);

      // Phase 3..N: "doo" — descending tail, repeated per syllable count.
      // Each extra syllable adds ~0.35s of tail with slight pitch variation.
      for (let s = 0; s < syllables; s++) {
        const tailStart = p2End - 0.02 + s * 0.35 * env;
        const tailEnd = tailStart + 0.5 * env;
        const g3 = ctx.createGain();
        const gainAmp = 0.18 * v.volumeMult * (s === 0 ? 1.0 : 0.65);
        g3.gain.setValueAtTime(0.001, t + tailStart);
        g3.gain.linearRampToValueAtTime(gainAmp, t + tailStart + 0.07 * env);
        g3.gain.exponentialRampToValueAtTime(0.001, t + tailEnd);
        g3.connect(dest);

        const lfo2 = ctx.createOscillator();
        lfo2.type = 'sine';
        lfo2.frequency.setValueAtTime(5, t + tailStart);
        const lfo2G = ctx.createGain();
        lfo2G.gain.setValueAtTime(30 * v.pitchMult, t + tailStart);
        lfo2.connect(lfo2G);

        const syllablePitch = 1 - s * 0.08; // subsequent syllables drop a half-step
        const o3 = ctx.createOscillator();
        o3.type = wave;
        o3.frequency.setValueAtTime(700 * v.pitchMult * syllablePitch, t + tailStart);
        o3.frequency.exponentialRampToValueAtTime(400 * v.pitchMult * syllablePitch, t + tailEnd);
        lfo2G.connect(o3.frequency);
        o3.connect(g3);
        o3.start(t + tailStart);
        o3.stop(t + tailEnd + 0.01);
        lfo2.start(t + tailStart);
        lfo2.stop(t + tailEnd + 0.01);
      }
    },

    /** Crystalline chime: sine 1400 Hz + 2100 Hz with shimmer LFO, 400ms */
    golden_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g1 = ctx.createGain();
      g1.gain.setValueAtTime(0.001, t);
      g1.gain.linearRampToValueAtTime(0.30 * v.volumeMult, t + 0.01);
      g1.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      g1.connect(dest);

      const o1 = ctx.createOscillator();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(1400 * v.pitchMult, t);
      o1.connect(g1);
      o1.start(t);
      o1.stop(t + 0.41);

      // Shimmer harmonic
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t);
      g2.gain.linearRampToValueAtTime(0.18 * v.volumeMult, t + 0.02);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
      g2.connect(dest);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(2100 * v.pitchMult, t);
      o2.connect(g2);
      o2.start(t);
      o2.stop(t + 0.36);

      // Shimmer LFO on upper harmonic pitch
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(8, t);
      const lfoG = ctx.createGain();
      lfoG.gain.setValueAtTime(15 * v.pitchMult, t);
      lfo.connect(lfoG);
      lfoG.connect(o2.frequency);
      lfo.start(t);
      lfo.stop(t + 0.36);
    },

    /** Dull thud: low sine 150→80 Hz + lowpass noise, 200ms */
    rotten_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(150 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(80 * v.pitchMult, t + 0.15);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.21);

      // Muffled noise layer
      const len = Math.floor(ctx.sampleRate * 0.1);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(400, t);

      const nG = ctx.createGain();
      nG.gain.setValueAtTime(0.001, t);
      nG.gain.linearRampToValueAtTime(0.1 * v.volumeMult, t + 0.005);
      nG.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

      noise.connect(lp);
      lp.connect(nG);
      nG.connect(dest);
      noise.start(t);
      noise.stop(t + 0.12);
    },

    /** Yolk Burst: satisfying descending 5-note cascade, warm sine */
    yolk_burst(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [1046.5, 880, 783.99, 659.25, 523.25];
      for (let i = 0; i < notes.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.06);
        g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + i * 0.06 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.06 + 0.25); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + i * 0.06);
        o.connect(g); o.start(t + i * 0.06); o.stop(t + i * 0.06 + 0.26);
      }
    },

    /** Egg Shower: rapid sparkle of 6 light plinking notes */
    egg_shower(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const freqs = [1300, 1800, 1100, 1600, 1400, 1900];
      for (let i = 0; i < freqs.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.04);
        g.gain.linearRampToValueAtTime(0.20 * v.volumeMult, t + i * 0.04 + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.04 + 0.12); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sine';
        o.frequency.setValueAtTime(freqs[i] * v.pitchMult, t + i * 0.04);
        o.connect(g); o.start(t + i * 0.04); o.stop(t + i * 0.04 + 0.13);
      }
    },

    /** Double Time: ascending whoosh, accelerating notes */
    double_time(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [392, 523.25, 659.25, 783.99, 1046.5, 1318.5];
      for (let i = 0; i < notes.length; i++) {
        const d = i * 0.035;
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + d);
        g.gain.linearRampToValueAtTime(0.22 * v.volumeMult, t + d + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, t + d + 0.15); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + d);
        o.connect(g); o.start(t + d); o.stop(t + d + 0.16);
      }
    },

    /** Warm two-note chime: C5 (523 Hz) then E5 (659 Hz), 800ms decay */
    milestone(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25];
      const offsets = [0, 0.2];

      for (let i = 0; i < notes.length; i++) {
        const nt = t + offsets[i];
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.35 * v.volumeMult, nt + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.6);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.61);
      }
    },

    /** Soft wooden click: triangle 400→200 Hz, 60ms */
    purchase(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(400 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(200 * v.pitchMult, t + 0.06);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.09);
    },

    /** Soft peep: sine sweep up 1800→2400→2000 Hz, 150ms */
    bonus_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(1800 * v.pitchMult, t);
      o.frequency.linearRampToValueAtTime(2400 * v.pitchMult, t + 0.04);
      o.frequency.exponentialRampToValueAtTime(2000 * v.pitchMult, t + 0.15);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.16);
    },

    /** Warm coo: low sine 340 Hz with 2 Hz FM vibrato, 400ms */
    broody_coo(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.03);
      g.gain.setValueAtTime(0.2 * v.volumeMult, t + 0.25);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      g.connect(dest);

      // FM vibrato at 2 Hz
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(2, t);
      const lfoG = ctx.createGain();
      lfoG.gain.setValueAtTime(20 * v.pitchMult, t);
      lfo.connect(lfoG);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(340 * v.pitchMult, t);
      lfoG.connect(o.frequency);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.41);
      lfo.start(t);
      lfo.stop(t + 0.41);
    },

    /** Ascending 3-note chime C5→E5→G5 with 2nd harmonics, 600ms */
    matriarch_promote(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25, 783.99];

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.12;

        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, nt + 0.015);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.5);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.51);

        // 2nd harmonic for richness
        const g2 = ctx.createGain();
        g2.gain.setValueAtTime(0.001, nt);
        g2.gain.linearRampToValueAtTime(0.08 * v.volumeMult, nt + 0.02);
        g2.gain.exponentialRampToValueAtTime(0.001, nt + 0.4);
        g2.connect(dest);

        const o2 = ctx.createOscillator();
        o2.type = 'sine';
        o2.frequency.setValueAtTime(notes[i] * 2 * v.pitchMult, nt);
        o2.connect(g2);
        o2.start(nt);
        o2.stop(nt + 0.41);
      }
    },

    /** Earthy squelch — low sine pop + filtered noise burst */
    worm_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      // Low body pop
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.22 * v.volumeMult, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(250 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(120 * v.pitchMult, t + 0.1);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.13);
      // Dirt noise layer
      const nLen = Math.floor(ctx.sampleRate * 0.06);
      const nBuf = ctx.createBuffer(1, nLen, ctx.sampleRate);
      const nD = nBuf.getChannelData(0);
      for (let i = 0; i < nLen; i++) nD[i] = Math.random() * 2 - 1;
      const ns = ctx.createBufferSource();
      ns.buffer = nBuf;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 600;
      const ng = ctx.createGain();
      ng.gain.setValueAtTime(0.001, t);
      ng.gain.linearRampToValueAtTime(0.12 * v.volumeMult, t + 0.005);
      ng.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
      ns.connect(lp);
      lp.connect(ng);
      ng.connect(dest);
      ns.start(t);
      ns.stop(t + 0.07);
    },

    /** Airy flutter — high sine trill with gentle shimmer */
    butterfly_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      // Quick ascending trill (3 micro-notes)
      const notes = [1200, 1500, 1800];
      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.04;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, nt + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.06);
        g.connect(dest);
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.frequency.linearRampToValueAtTime(notes[i] * 1.1 * v.pitchMult, nt + 0.05);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.07);
      }
    }
  },

  // ─── ARCADE — playful, bouncy, cartoon, video-game-y ────────────
  arcade: {

    /** Triangle sweep 1200→600 Hz, snappy attack, ~100ms */
    egg_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.35 * v.volumeMult, t + 0.003);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(1200 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(600 * v.pitchMult, t + 0.08);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.11);
    },

    /** Bandpass noise burst, center 800 Hz Q=8, ~120ms */
    hen_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const len = Math.floor(ctx.sampleRate * 0.12);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(800 * v.pitchMult, t);
      bp.Q.setValueAtTime(8, t);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.4 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);

      noise.connect(bp);
      bp.connect(g);
      g.connect(dest);
      noise.start(t);
      noise.stop(t + 0.12);
    },

    /** Arcade rooster cluck — lower bandpass (500 Hz), 3 variants, repeat pattern */
    rooster_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const envMult = v.crowEnv || 1.0;
      const r = Math.random();
      const syllables = r < 0.50 ? 3 : r < 0.75 ? 2 : 4;
      const gap = (r < 0.50 ? 0.11 : r < 0.75 ? 0.14 : 0.08) * envMult;
      const sylDur = (r < 0.75 ? 0.10 : 0.07) * envMult;
      const centerHz = r >= 0.75 ? 650 : 500;
      const vol = r < 0.50 ? 1.0 : r < 0.75 ? 0.75 : 1.05;

      for (let s = 0; s < syllables; s++) {
        const st = t + s * gap;
        const len = Math.floor(ctx.sampleRate * sylDur);
        const buf = ctx.createBuffer(1, len, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

        const noise = ctx.createBufferSource();
        noise.buffer = buf;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.setValueAtTime(centerHz * v.pitchMult, st);
        bp.Q.setValueAtTime(6, st);

        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, st);
        g.gain.linearRampToValueAtTime(0.35 * vol * v.volumeMult, st + 0.004);
        g.gain.exponentialRampToValueAtTime(0.001, st + sylDur);

        noise.connect(bp);
        bp.connect(g);
        g.connect(dest);
        noise.start(st);
        noise.stop(st + sylDur + 0.01);
      }
    },

    /** FM synthesis: sine 600→1200 Hz sweep, 6 Hz vibrato, ~0.8s */
    rooster_crow(ctx, dest, v) {
      // v1.4 Option C Polish+ — arcade reads all 6 CROW_PATCHES axes.
      const syllables = Math.max(1, Math.min(3, v.crowSyllables || 1));
      const env = v.crowEnv || 1.0;
      const bright = v.crowBright || 1.0;
      const wave = v.crowWaveform || 'sine';
      const baseDur = 0.8 * env;

      for (let s = 0; s < syllables; s++) {
        const t = ctx.currentTime + Math.max(0, v.delay) + s * baseDur * 1.05;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.35 * v.volumeMult * bright, t + 0.02);
        g.gain.setValueAtTime(0.3 * v.volumeMult * bright, t + baseDur * 0.6);
        g.gain.exponentialRampToValueAtTime(0.001, t + baseDur);
        g.connect(dest);

        const mod = ctx.createOscillator();
        mod.type = 'sine';
        mod.frequency.setValueAtTime(6, t);
        const modG = ctx.createGain();
        modG.gain.setValueAtTime(40 * bright, t);
        mod.connect(modG);

        const o = ctx.createOscillator();
        o.type = wave;
        o.frequency.setValueAtTime(600 * v.pitchMult, t);
        o.frequency.linearRampToValueAtTime(1200 * v.pitchMult, t + baseDur * 0.5);
        o.frequency.linearRampToValueAtTime(900 * v.pitchMult, t + baseDur);
        modG.connect(o.frequency);
        o.connect(g);
        o.start(t);
        o.stop(t + baseDur + 0.01);
        mod.start(t);
        mod.stop(t + baseDur + 0.01);
      }
    },

    /** 3-note arpeggio C5-E5-G5, bright triangle, fast staccato, ~300ms */
    golden_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25, 783.99];

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.08;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.3 * v.volumeMult, nt + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.1);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.11);
      }
    },

    /** Cartoon splat: bandpass noise 400→100 Hz descending, ~180ms */
    rotten_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const len = Math.floor(ctx.sampleRate * 0.18);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(400 * v.pitchMult, t);
      bp.frequency.exponentialRampToValueAtTime(100 * v.pitchMult, t + 0.15);
      bp.Q.setValueAtTime(3, t);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.4 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);

      noise.connect(bp);
      bp.connect(g);
      g.connect(dest);
      noise.start(t);
      noise.stop(t + 0.18);
    },

    yolk_burst(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [1046.5, 880, 783.99, 659.25, 523.25];
      for (let i = 0; i < notes.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.05);
        g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + i * 0.05 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.05 + 0.2); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'triangle';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + i * 0.05);
        o.connect(g); o.start(t + i * 0.05); o.stop(t + i * 0.05 + 0.21);
      }
    },
    egg_shower(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const freqs = [1300, 1800, 1100, 1600, 1400, 1900];
      for (let i = 0; i < freqs.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.035);
        g.gain.linearRampToValueAtTime(0.12 * v.volumeMult, t + i * 0.035 + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.035 + 0.1); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'triangle';
        o.frequency.setValueAtTime(freqs[i] * v.pitchMult, t + i * 0.035);
        o.connect(g); o.start(t + i * 0.035); o.stop(t + i * 0.035 + 0.11);
      }
    },
    double_time(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [392, 523.25, 659.25, 783.99, 1046.5, 1318.5];
      for (let i = 0; i < notes.length; i++) {
        const d = i * 0.03;
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + d);
        g.gain.linearRampToValueAtTime(0.16 * v.volumeMult, t + d + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, t + d + 0.12); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'triangle';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + d);
        o.connect(g); o.start(t + d); o.stop(t + d + 0.13);
      }
    },

    /** Fanfare sting: 4-note ascending arpeggio C5-E5-G5-C6, square, ~500ms */
    milestone(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25, 783.99, 1046.5];

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.07;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.18 * v.volumeMult, nt + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.3);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.31);
      }
    },

    /** Coin collect: sine 1800 Hz + 2nd harmonic, bright, ~60ms */
    purchase(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.3 * v.volumeMult, t + 0.003);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.06);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(1800 * v.pitchMult, t);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.07);

      // 2nd harmonic
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t);
      g2.gain.linearRampToValueAtTime(0.12 * v.volumeMult, t + 0.003);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.05);
      g2.connect(dest);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(3600 * v.pitchMult, t);
      o2.connect(g2);
      o2.start(t);
      o2.stop(t + 0.06);
    },

    /** Bouncy boing: triangle 600→1200→400 Hz pitch bounce, ~120ms */
    bonus_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.3 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.12);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(600 * v.pitchMult, t);
      o.frequency.linearRampToValueAtTime(1200 * v.pitchMult, t + 0.03);
      o.frequency.exponentialRampToValueAtTime(400 * v.pitchMult, t + 0.12);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.13);
    },

    /** Gentle descending hum: muted triangle 500→300 Hz, sleepy, ~300ms */
    broody_coo(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.02);
      g.gain.setValueAtTime(0.18 * v.volumeMult, t + 0.15);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(500 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(300 * v.pitchMult, t + 0.3);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.31);
    },

    /** Level-up sting: 4-note ascending arpeggio, bright triangle, ~400ms */
    matriarch_promote(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25, 783.99, 1046.5];

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.06;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, nt + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.25);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.26);
      }
    },

    /** Cartoon squelch — bouncy descending bloop */
    worm_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.3 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(500 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(150 * v.pitchMult, t + 0.12);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.16);
    },

    /** Sparkly whoosh — fast ascending triangle sweep */
    butterfly_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(800 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(2400 * v.pitchMult, t + 0.15);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.21);
    }
  },

  // ─── 8-BIT — NES/chiptune, square waves, retro blips ────────────
  '8bit': {

    /** Square pulse drop 880→440 Hz, very short NES blip */
    egg_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.3 * v.volumeMult, t + 0.002);
      g.gain.setValueAtTime(0.3 * v.volumeMult, t + 0.04);
      g.gain.linearRampToValueAtTime(0.001, t + 0.08);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(880 * v.pitchMult, t);
      o.frequency.setValueAtTime(440 * v.pitchMult, t + 0.04);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.09);
    },

    /** Square pulse burst with fast pitch wobble — 8-bit cluck */
    hen_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.003);
      g.gain.setValueAtTime(0.2 * v.volumeMult, t + 0.06);
      g.gain.linearRampToValueAtTime(0.001, t + 0.1);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(600 * v.pitchMult, t);
      o.frequency.setValueAtTime(400 * v.pitchMult, t + 0.03);
      o.frequency.setValueAtTime(500 * v.pitchMult, t + 0.06);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.11);
    },

    /** 8-bit rooster cluck — square pulses at 350 Hz, 3 variants */
    rooster_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const envMult = v.crowEnv || 1.0;
      const r = Math.random();
      const syllables = r < 0.50 ? 3 : r < 0.75 ? 2 : 4;
      const gap = (r < 0.50 ? 0.11 : r < 0.75 ? 0.14 : 0.08) * envMult;
      const sylDur = (r < 0.75 ? 0.08 : 0.06) * envMult;
      const f0 = r >= 0.75 ? 440 : 340;
      const f1 = r >= 0.75 ? 320 : 220;
      const vol = r < 0.50 ? 1.0 : r < 0.75 ? 0.75 : 1.05;

      for (let s = 0; s < syllables; s++) {
        const st = t + s * gap;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, st);
        g.gain.linearRampToValueAtTime(0.25 * vol * v.volumeMult, st + 0.003);
        g.gain.setValueAtTime(0.18 * vol * v.volumeMult, st + sylDur * 0.6);
        g.gain.linearRampToValueAtTime(0.001, st + sylDur);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.setValueAtTime(f0 * v.pitchMult, st);
        o.frequency.setValueAtTime(f1 * v.pitchMult, st + sylDur * 0.5);
        o.connect(g);
        o.start(st);
        o.stop(st + sylDur + 0.01);
      }
    },

    /** NES-style rising square wave with fast vibrato, ~0.7s */
    rooster_crow(ctx, dest, v) {
      // v1.4 Option C Polish+ — 8bit reads all 6 CROW_PATCHES axes.
      const syllables = Math.max(1, Math.min(3, v.crowSyllables || 1));
      const env = v.crowEnv || 1.0;
      const bright = v.crowBright || 1.0;
      const wave = v.crowWaveform || 'square';
      const baseDur = 0.7 * env;

      for (let s = 0; s < syllables; s++) {
        const t = ctx.currentTime + Math.max(0, v.delay) + s * baseDur * 1.05;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.3 * v.volumeMult * bright, t + 0.01);
        g.gain.setValueAtTime(0.25 * v.volumeMult * bright, t + baseDur * 0.7);
        g.gain.linearRampToValueAtTime(0.001, t + baseDur);
        g.connect(dest);

        const lfo = ctx.createOscillator();
        lfo.type = 'square';
        lfo.frequency.setValueAtTime(8, t);
        const lfoG = ctx.createGain();
        lfoG.gain.setValueAtTime(25 * bright, t);
        lfo.connect(lfoG);

        const o = ctx.createOscillator();
        o.type = wave;
        o.frequency.setValueAtTime(440 * v.pitchMult, t);
        o.frequency.linearRampToValueAtTime(880 * v.pitchMult, t + baseDur * 0.4);
        o.frequency.linearRampToValueAtTime(660 * v.pitchMult, t + baseDur);
        lfoG.connect(o.frequency);
        o.connect(g);
        o.start(t);
        o.stop(t + baseDur + 0.01);
        lfo.start(t);
        lfo.stop(t + baseDur + 0.01);
      }
    },

    /** NES power-up: rapid ascending 4-note arpeggio, square */
    golden_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25, 783.99, 1046.5];
      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.05;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, nt + 0.002);
        g.gain.setValueAtTime(0.25 * v.volumeMult, nt + 0.04);
        g.gain.linearRampToValueAtTime(0.001, nt + 0.08);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.09);
      }
    },

    /** NES damage hit: descending square 300→80 Hz, harsh */
    rotten_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.35 * v.volumeMult, t + 0.003);
      g.gain.linearRampToValueAtTime(0.001, t + 0.15);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(300 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(80 * v.pitchMult, t + 0.12);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.16);
    },

    yolk_burst(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [1046.5, 880, 783.99, 659.25, 523.25];
      for (let i = 0; i < notes.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.04);
        g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + i * 0.04 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.04 + 0.18); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'square';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + i * 0.04);
        o.connect(g); o.start(t + i * 0.04); o.stop(t + i * 0.04 + 0.19);
      }
    },
    egg_shower(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const freqs = [1300, 1800, 1100, 1600, 1400, 1900];
      for (let i = 0; i < freqs.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.03);
        g.gain.linearRampToValueAtTime(0.1 * v.volumeMult, t + i * 0.03 + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.03 + 0.08); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'square';
        o.frequency.setValueAtTime(freqs[i] * v.pitchMult, t + i * 0.03);
        o.connect(g); o.start(t + i * 0.03); o.stop(t + i * 0.03 + 0.09);
      }
    },
    double_time(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [392, 523.25, 659.25, 783.99, 1046.5, 1318.5];
      for (let i = 0; i < notes.length; i++) {
        const d = i * 0.025;
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + d);
        g.gain.linearRampToValueAtTime(0.12 * v.volumeMult, t + d + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, t + d + 0.1); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'square';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + d);
        o.connect(g); o.start(t + d); o.stop(t + d + 0.11);
      }
    },

    /** NES fanfare: ascending major scale C5-D5-E5-F5-G5-C6, square */
    milestone(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 587.33, 659.25, 783.99, 1046.5];
      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.08;
        const dur = i === notes.length - 1 ? 0.3 : 0.07;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, nt + 0.002);
        g.gain.setValueAtTime(0.2 * v.volumeMult, nt + dur);
        g.gain.linearRampToValueAtTime(0.001, nt + dur + 0.05);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + dur + 0.06);
      }
    },

    /** Quick coin blip: square 1200 Hz, 40ms */
    purchase(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.002);
      g.gain.setValueAtTime(0.25 * v.volumeMult, t + 0.03);
      g.gain.linearRampToValueAtTime(0.001, t + 0.05);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(1200 * v.pitchMult, t);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.06);
    },

    /** Quick chirpy up-down blip: square 800→1200→600, 100ms */
    bonus_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.003);
      g.gain.linearRampToValueAtTime(0.001, t + 0.1);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(800 * v.pitchMult, t);
      o.frequency.setValueAtTime(1200 * v.pitchMult, t + 0.03);
      o.frequency.setValueAtTime(600 * v.pitchMult, t + 0.06);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.11);
    },

    /** Low square hum with slow pulse — sleepy NES lullaby feel, 350ms */
    broody_coo(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + 0.02);
      g.gain.setValueAtTime(0.15 * v.volumeMult, t + 0.2);
      g.gain.linearRampToValueAtTime(0.001, t + 0.35);
      g.connect(dest);

      const lfo = ctx.createOscillator();
      lfo.type = 'square';
      lfo.frequency.setValueAtTime(3, t);
      const lfoG = ctx.createGain();
      lfoG.gain.setValueAtTime(0.08, t);
      lfo.connect(lfoG);
      lfoG.connect(g.gain);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(220 * v.pitchMult, t);
      o.frequency.setValueAtTime(200 * v.pitchMult, t + 0.15);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.36);
      lfo.start(t);
      lfo.stop(t + 0.36);
    },

    /** NES level-complete: C5-E5-G5-C6 with octave harmony, square */
    matriarch_promote(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 659.25, 783.99, 1046.5];
      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.07;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.22 * v.volumeMult, nt + 0.002);
        g.gain.setValueAtTime(0.22 * v.volumeMult, nt + 0.06);
        g.gain.linearRampToValueAtTime(0.001, nt + 0.3);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.31);

        // Lower octave harmony
        const g2 = ctx.createGain();
        g2.gain.setValueAtTime(0.001, nt);
        g2.gain.linearRampToValueAtTime(0.1 * v.volumeMult, nt + 0.002);
        g2.gain.linearRampToValueAtTime(0.001, nt + 0.2);
        g2.connect(dest);

        const o2 = ctx.createOscillator();
        o2.type = 'square';
        o2.frequency.setValueAtTime(notes[i] * 0.5 * v.pitchMult, nt);
        o2.connect(g2);
        o2.start(nt);
        o2.stop(nt + 0.21);
      }
    },

    /** NES dirt blip — descending square pulse */
    worm_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.002);
      g.gain.setValueAtTime(0.25 * v.volumeMult, t + 0.04);
      g.gain.linearRampToValueAtTime(0.001, t + 0.08);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.setValueAtTime(300 * v.pitchMult, t);
      o.frequency.linearRampToValueAtTime(100 * v.pitchMult, t + 0.06);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.09);
    },

    /** 8-bit flutter — fast ascending 2-note chirp */
    butterfly_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [880, 1320];
      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.04;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, nt + 0.002);
        g.gain.setValueAtTime(0.2 * v.volumeMult, nt + 0.03);
        g.gain.linearRampToValueAtTime(0.001, nt + 0.05);
        g.connect(dest);
        const o = ctx.createOscillator();
        o.type = 'square';
        o.frequency.value = notes[i] * v.pitchMult;
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.06);
      }
    }
  },

  // ─── LO-FI — warm, muffled, tape-wobble, chill ──────────────────
  lofi: {

    /** Muffled tap: low-pass filtered triangle 600→400 Hz, slow attack */
    egg_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(1200, t);
      lp.Q.setValueAtTime(1, t);
      lp.connect(dest);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      g.connect(lp);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(600 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(400 * v.pitchMult, t + 0.15);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.19);
    },

    /** Warm fuzzy cluck: detuned sine pair + filtered noise */
    hen_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(900, t);
      lp.connect(dest);

      // Detuned pair
      for (const detune of [-6, 6]) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
        g.connect(lp);

        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(300 * v.pitchMult, t);
        o.detune.setValueAtTime(detune, t);
        o.frequency.exponentialRampToValueAtTime(220 * v.pitchMult, t + 0.12);
        o.connect(g);
        o.start(t);
        o.stop(t + 0.19);
      }
    },

    /** Lo-fi rooster cluck — warm muffled, deeper detuned pair, 3 variants */
    rooster_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const envMult = v.crowEnv || 1.0;
      const r = Math.random();
      const syllables = r < 0.50 ? 3 : r < 0.75 ? 2 : 4;
      const gap = (r < 0.50 ? 0.15 : r < 0.75 ? 0.18 : 0.10) * envMult;
      const sylDur = (r < 0.75 ? 0.16 : 0.10) * envMult;
      const f0 = r >= 0.75 ? 260 : 210;
      const f1 = r >= 0.75 ? 190 : 140;
      const vol = r < 0.50 ? 1.0 : r < 0.75 ? 0.75 : 1.05;

      for (let s = 0; s < syllables; s++) {
        const st = t + s * gap;
        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.setValueAtTime(750, st);
        lp.connect(dest);

        for (const detune of [-6, 6]) {
          const g = ctx.createGain();
          g.gain.setValueAtTime(0.001, st);
          g.gain.linearRampToValueAtTime(0.14 * vol * v.volumeMult, st + 0.008);
          g.gain.exponentialRampToValueAtTime(0.001, st + sylDur);
          g.connect(lp);

          const o = ctx.createOscillator();
          o.type = 'sine';
          o.frequency.setValueAtTime(f0 * v.pitchMult, st);
          o.detune.setValueAtTime(detune, st);
          o.frequency.exponentialRampToValueAtTime(f1 * v.pitchMult, st + sylDur * 0.7);
          o.connect(g);
          o.start(st);
          o.stop(st + sylDur + 0.02);
        }
      }
    },

    /** Warm detuned triangle layers with slow vibrato, tape-like, ~0.9s */
    rooster_crow(ctx, dest, v) {
      // v1.4 Option C Polish+ — lofi reads all 6 CROW_PATCHES axes.
      const syllables = Math.max(1, Math.min(3, v.crowSyllables || 1));
      const env = v.crowEnv || 1.0;
      const bright = v.crowBright || 1.0;
      const wave = v.crowWaveform || 'triangle';
      const baseDur = 0.9 * env;

      for (let s = 0; s < syllables; s++) {
        const t = ctx.currentTime + Math.max(0, v.delay) + s * baseDur * 1.05;

        const lp = ctx.createBiquadFilter();
        lp.type = 'lowpass';
        lp.frequency.setValueAtTime(1500 + 500 * bright, t);
        lp.connect(dest);

        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.25 * v.volumeMult * bright, t + 0.06);
        g.gain.setValueAtTime(0.2 * v.volumeMult * bright, t + baseDur * 0.6);
        g.gain.exponentialRampToValueAtTime(0.001, t + baseDur);
        g.connect(lp);

        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(3.5, t);
        const lfoG = ctx.createGain();
        lfoG.gain.setValueAtTime(8, t);
        lfo.connect(lfoG);

        for (const detune of [-8, 0, 8]) {
          const o = ctx.createOscillator();
          o.type = wave;
          o.frequency.setValueAtTime(320 * v.pitchMult, t);
          o.detune.setValueAtTime(detune, t);
          o.frequency.exponentialRampToValueAtTime(640 * v.pitchMult, t + baseDur * 0.4);
          o.frequency.exponentialRampToValueAtTime(480 * v.pitchMult, t + baseDur);
          lfoG.connect(o.frequency);
          o.connect(g);
          o.start(t);
          o.stop(t + baseDur + 0.01);
        }
        lfo.start(t);
        lfo.stop(t + baseDur + 0.01);
      }
    },

    /** Warm filtered bell: detuned sine pair, heavy low-pass, 500ms */
    golden_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(2000, t);
      lp.frequency.exponentialRampToValueAtTime(800, t + 0.4);
      lp.connect(dest);

      for (const [freq, vol] of [[1100, 0.2], [1108, 0.15], [1650, 0.1]]) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(vol * v.volumeMult, t + 0.025);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
        g.connect(lp);

        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(freq * v.pitchMult, t);
        o.connect(g);
        o.start(t);
        o.stop(t + 0.51);
      }
    },

    /** Muffled thud: heavily filtered sine 120→60 Hz, tape-like */
    rotten_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(300, t);
      lp.connect(dest);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.35 * v.volumeMult, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      g.connect(lp);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(120 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(60 * v.pitchMult, t + 0.2);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.26);
    },

    yolk_burst(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [523.25, 440, 392, 329.63, 261.63];
      for (let i = 0; i < notes.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.07);
        g.gain.linearRampToValueAtTime(0.18 * v.volumeMult, t + i * 0.07 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.3); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + i * 0.07);
        o.connect(g); o.start(t + i * 0.07); o.stop(t + i * 0.07 + 0.31);
      }
    },
    egg_shower(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const freqs = [800, 1100, 700, 1000, 900, 1200];
      for (let i = 0; i < freqs.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.05);
        g.gain.linearRampToValueAtTime(0.12 * v.volumeMult, t + i * 0.05 + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.05 + 0.15); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sine';
        o.frequency.setValueAtTime(freqs[i] * v.pitchMult, t + i * 0.05);
        o.connect(g); o.start(t + i * 0.05); o.stop(t + i * 0.05 + 0.16);
      }
    },
    double_time(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [261.63, 329.63, 392, 523.25, 659.25, 783.99];
      for (let i = 0; i < notes.length; i++) {
        const d = i * 0.045;
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + d);
        g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + d + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + d + 0.18); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + d);
        o.connect(g); o.start(t + d); o.stop(t + d + 0.19);
      }
    },

    /** Gentle filtered two-note: C4→E4, warm and chill, long decay */
    milestone(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [261.63, 329.63];

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(1500, t);
      lp.connect(dest);

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.3;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, nt + 0.04);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.9);
        g.connect(lp);

        // Detuned pair for warmth
        for (const detune of [-5, 5]) {
          const o = ctx.createOscillator();
          o.type = 'sine';
          o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
          o.detune.setValueAtTime(detune, nt);
          o.connect(g);
          o.start(nt);
          o.stop(nt + 0.91);
        }
      }
    },

    /** Soft filtered click: triangle 500→300 Hz, very muted */
    purchase(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(800, t);
      lp.connect(dest);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      g.connect(lp);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(500 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(300 * v.pitchMult, t + 0.08);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.11);
    },

    /** Gentle warm blip: filtered triangle sweep up, soft */
    bonus_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(1800, t);
      lp.connect(dest);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.18 * v.volumeMult, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(lp);

      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(1400 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(1800 * v.pitchMult, t + 0.06);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.16);
    },

    /** Warm ASMR-like hum: detuned sines with very slow LFO, 500ms */
    broody_coo(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(600, t);
      lp.connect(dest);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + 0.05);
      g.gain.setValueAtTime(0.15 * v.volumeMult, t + 0.3);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      g.connect(lp);

      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(1.5, t);
      const lfoG = ctx.createGain();
      lfoG.gain.setValueAtTime(10, t);
      lfo.connect(lfoG);

      for (const detune of [-8, 0, 8]) {
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(280 * v.pitchMult, t);
        o.detune.setValueAtTime(detune, t);
        lfoG.connect(o.frequency);
        o.connect(g);
        o.start(t);
        o.stop(t + 0.51);
      }
      lfo.start(t);
      lfo.stop(t + 0.51);
    },

    /** Warm ascending notes C4-E4-G4, filtered, long lo-fi decay */
    matriarch_promote(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [261.63, 329.63, 392.0];

      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.setValueAtTime(1800, t);
      lp.frequency.exponentialRampToValueAtTime(900, t + 0.8);
      lp.connect(dest);

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.18;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.18 * v.volumeMult, nt + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.7);
        g.connect(lp);

        for (const detune of [-5, 5]) {
          const o = ctx.createOscillator();
          o.type = 'sine';
          o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
          o.detune.setValueAtTime(detune, nt);
          o.connect(g);
          o.start(nt);
          o.stop(nt + 0.71);
        }
      }
    },

    /** Muffled squelch — LP-filtered sine drop with tape wobble */
    worm_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 500;
      lp.connect(dest);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.015);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(lp);
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.setValueAtTime(280 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(140 * v.pitchMult, t + 0.12);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.16);
    },

    /** Gentle ping — muffled sine with slow attack, dreamy */
    butterfly_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 1200;
      lp.connect(dest);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.18 * v.volumeMult, t + 0.03);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      g.connect(lp);
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(900 * v.pitchMult, t);
      o.frequency.linearRampToValueAtTime(1100 * v.pitchMult, t + 0.25);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.31);
    }
  },

  // ─── SPOOKY — eerie, minor key, theremin, Halloween ────────��────
  spooky: {

    /** Minor descending tone: sine with dissonant 2nd, tritone interval */
    egg_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
      g.connect(dest);

      const o1 = ctx.createOscillator();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(800 * v.pitchMult, t);
      o1.frequency.exponentialRampToValueAtTime(560 * v.pitchMult, t + 0.15);
      o1.connect(g);
      o1.start(t);
      o1.stop(t + 0.19);

      // Tritone dissonance
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t);
      g2.gain.linearRampToValueAtTime(0.08 * v.volumeMult, t + 0.01);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g2.connect(dest);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(800 * Math.SQRT2 * v.pitchMult, t);
      o2.frequency.exponentialRampToValueAtTime(560 * Math.SQRT2 * v.pitchMult, t + 0.15);
      o2.connect(g2);
      o2.start(t);
      o2.stop(t + 0.16);
    },

    /** Ghostly cluck: filtered noise with eerie pitch sweep */
    hen_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const len = Math.floor(ctx.sampleRate * 0.15);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(1000 * v.pitchMult, t);
      bp.frequency.exponentialRampToValueAtTime(400 * v.pitchMult, t + 0.12);
      bp.Q.setValueAtTime(12, t);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);

      noise.connect(bp);
      bp.connect(g);
      g.connect(dest);
      noise.start(t);
      noise.stop(t + 0.15);
    },

    /** Spooky rooster cluck — bandpass noise with eerie pitch sweep, 3 variants */
    rooster_cluck(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const envMult = v.crowEnv || 1.0;
      const r = Math.random();
      const syllables = r < 0.50 ? 3 : r < 0.75 ? 2 : 4;
      const gap = (r < 0.50 ? 0.14 : r < 0.75 ? 0.18 : 0.09) * envMult;
      const sylDur = (r < 0.75 ? 0.12 : 0.08) * envMult;
      const fStart = r >= 0.75 ? 850 : 650;
      const fEnd = r >= 0.75 ? 350 : 250;
      const vol = r < 0.50 ? 1.0 : r < 0.75 ? 0.75 : 1.05;

      for (let s = 0; s < syllables; s++) {
        const st = t + s * gap;
        const len = Math.floor(ctx.sampleRate * sylDur);
        const buf = ctx.createBuffer(1, len, ctx.sampleRate);
        const d = buf.getChannelData(0);
        for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

        const noise = ctx.createBufferSource();
        noise.buffer = buf;
        const bp = ctx.createBiquadFilter();
        bp.type = 'bandpass';
        bp.frequency.setValueAtTime(fStart * v.pitchMult, st);
        bp.frequency.exponentialRampToValueAtTime(fEnd * v.pitchMult, st + sylDur * 0.8);
        bp.Q.setValueAtTime(10, st);

        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, st);
        g.gain.linearRampToValueAtTime(0.18 * vol * v.volumeMult, st + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, st + sylDur);

        noise.connect(bp);
        bp.connect(g);
        g.connect(dest);
        noise.start(st);
        noise.stop(st + sylDur + 0.01);
      }
    },

    /** Theremin wail: sine with heavy vibrato, rising then falling, eerie */
    rooster_crow(ctx, dest, v) {
      // v1.4 Option C Polish+ — spooky reads all 6 CROW_PATCHES axes.
      const syllables = Math.max(1, Math.min(3, v.crowSyllables || 1));
      const env = v.crowEnv || 1.0;
      const bright = v.crowBright || 1.0;
      const wave = v.crowWaveform || 'sine';
      const baseDur = 1.0 * env;

      for (let s = 0; s < syllables; s++) {
        const t = ctx.currentTime + Math.max(0, v.delay) + s * baseDur * 1.05;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.25 * v.volumeMult * bright, t + 0.08);
        g.gain.setValueAtTime(0.2 * v.volumeMult * bright, t + baseDur * 0.6);
        g.gain.exponentialRampToValueAtTime(0.001, t + baseDur);
        g.connect(dest);

        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(5, t);
        lfo.frequency.linearRampToValueAtTime(7, t + baseDur);
        const lfoG = ctx.createGain();
        lfoG.gain.setValueAtTime(30 * v.pitchMult * bright, t);
        lfo.connect(lfoG);

        const o = ctx.createOscillator();
        o.type = wave;
        o.frequency.setValueAtTime(300 * v.pitchMult, t);
        o.frequency.exponentialRampToValueAtTime(700 * v.pitchMult, t + baseDur * 0.4);
        o.frequency.exponentialRampToValueAtTime(350 * v.pitchMult, t + baseDur);
        lfoG.connect(o.frequency);
        o.connect(g);
        o.start(t);
        o.stop(t + baseDur + 0.01);
        lfo.start(t);
        lfo.stop(t + baseDur + 0.01);
      }
    },

    /** Eerie crystalline: minor 3rd interval with ring-mod shimmer */
    golden_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      // Ring modulator via gain AM
      const ringMod = ctx.createOscillator();
      ringMod.type = 'sine';
      ringMod.frequency.setValueAtTime(12, t);
      const ringG = ctx.createGain();
      ringG.gain.setValueAtTime(0.3, t);
      ringMod.connect(ringG);

      // Root note (A4)
      const g1 = ctx.createGain();
      g1.gain.setValueAtTime(0.001, t);
      g1.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.02);
      g1.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      ringG.connect(g1.gain);
      g1.connect(dest);

      const o1 = ctx.createOscillator();
      o1.type = 'sine';
      o1.frequency.setValueAtTime(1100 * v.pitchMult, t);
      o1.connect(g1);
      o1.start(t);
      o1.stop(t + 0.51);

      // Minor 3rd above
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t + 0.05);
      g2.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + 0.07);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.45);
      g2.connect(dest);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(1100 * 1.2 * v.pitchMult, t);
      o2.connect(g2);
      o2.start(t + 0.05);
      o2.stop(t + 0.46);

      ringMod.start(t);
      ringMod.stop(t + 0.51);
    },

    /** Deep rumble with dissonant overtone — ominous */
    rotten_egg(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      // Deep rumble
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.35 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(100 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(50 * v.pitchMult, t + 0.25);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.31);

      // Dissonant overtone (tritone)
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t);
      g2.gain.linearRampToValueAtTime(0.1 * v.volumeMult, t + 0.01);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
      g2.connect(dest);

      const o2 = ctx.createOscillator();
      o2.type = 'sine';
      o2.frequency.setValueAtTime(141 * v.pitchMult, t);
      o2.frequency.exponentialRampToValueAtTime(71 * v.pitchMult, t + 0.18);
      o2.connect(g2);
      o2.start(t);
      o2.stop(t + 0.21);
    },

    yolk_burst(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [830.61, 698.46, 622.25, 523.25, 415.3];
      for (let i = 0; i < notes.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.07);
        g.gain.linearRampToValueAtTime(0.17 * v.volumeMult, t + i * 0.07 + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.28); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sawtooth';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + i * 0.07);
        o.connect(g); o.start(t + i * 0.07); o.stop(t + i * 0.07 + 0.29);
      }
    },
    egg_shower(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const freqs = [1000, 1400, 800, 1200, 1100, 1500];
      for (let i = 0; i < freqs.length; i++) {
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + i * 0.045);
        g.gain.linearRampToValueAtTime(0.1 * v.volumeMult, t + i * 0.045 + 0.005);
        g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.045 + 0.1); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sawtooth';
        o.frequency.setValueAtTime(freqs[i] * v.pitchMult, t + i * 0.045);
        o.connect(g); o.start(t + i * 0.045); o.stop(t + i * 0.045 + 0.11);
      }
    },
    double_time(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [311.13, 415.3, 523.25, 622.25, 830.61, 1046.5];
      for (let i = 0; i < notes.length; i++) {
        const d = i * 0.035;
        const g = ctx.createGain(); g.gain.setValueAtTime(0.001, t + d);
        g.gain.linearRampToValueAtTime(0.14 * v.volumeMult, t + d + 0.008);
        g.gain.exponentialRampToValueAtTime(0.001, t + d + 0.13); g.connect(dest);
        const o = ctx.createOscillator(); o.type = 'sawtooth';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, t + d);
        o.connect(g); o.start(t + d); o.stop(t + d + 0.14);
      }
    },

    /** Unsettling minor fanfare: Eb-Gb-A (diminished triad), slow */
    milestone(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [311.13, 369.99, 440.0]; // Eb5-Gb5-A5 diminished

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.25;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, nt + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.8);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.81);

        // Eerie vibrato on each note
        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(4, nt);
        const lfoG = ctx.createGain();
        lfoG.gain.setValueAtTime(8 * v.pitchMult, nt);
        lfo.connect(lfoG);
        lfoG.connect(o.frequency);
        lfo.start(nt);
        lfo.stop(nt + 0.81);
      }
    },

    /** Quick minor blip: descending minor 3rd */
    purchase(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(660 * v.pitchMult, t);
      o.frequency.setValueAtTime(550 * v.pitchMult, t + 0.04);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.11);
    },

    /** Ghost whisper: narrow bandpass noise sweep down */
    bonus_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const len = Math.floor(ctx.sampleRate * 0.2);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;

      const noise = ctx.createBufferSource();
      noise.buffer = buf;

      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.setValueAtTime(2000 * v.pitchMult, t);
      bp.frequency.exponentialRampToValueAtTime(600 * v.pitchMult, t + 0.18);
      bp.Q.setValueAtTime(15, t);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);

      noise.connect(bp);
      bp.connect(g);
      g.connect(dest);
      noise.start(t);
      noise.stop(t + 0.2);
    },

    /** Deep ominous hum: low sine with slow vibrato, creepy */
    broody_coo(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, t + 0.04);
      g.gain.setValueAtTime(0.18 * v.volumeMult, t + 0.3);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
      g.connect(dest);

      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.setValueAtTime(2.5, t);
      const lfoG = ctx.createGain();
      lfoG.gain.setValueAtTime(15, t);
      lfo.connect(lfoG);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(180 * v.pitchMult, t);
      lfoG.connect(o.frequency);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.51);
      lfo.start(t);
      lfo.stop(t + 0.51);
    },

    /** Dark ascending minor arpeggio: C-Eb-Gb-Bbb (diminished 7th) */
    matriarch_promote(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [261.63, 311.13, 369.99, 440.0]; // dim7

      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.15;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.2 * v.volumeMult, nt + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.5);
        g.connect(dest);

        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.51);

        const lfo = ctx.createOscillator();
        lfo.type = 'sine';
        lfo.frequency.setValueAtTime(5, nt);
        const lG = ctx.createGain();
        lG.gain.setValueAtTime(6 * v.pitchMult, nt);
        lfo.connect(lG);
        lG.connect(o.frequency);
        lfo.start(nt);
        lfo.stop(nt + 0.51);
      }
    },

    /** Creepy squelch — descending sine with ring mod buzz */
    worm_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.25 * v.volumeMult, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
      g.connect(dest);
      // Main tone
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(300 * v.pitchMult, t);
      o.frequency.exponentialRampToValueAtTime(80 * v.pitchMult, t + 0.12);
      o.connect(g);
      o.start(t);
      o.stop(t + 0.16);
      // Buzz overtone
      const g2 = ctx.createGain();
      g2.gain.setValueAtTime(0.001, t);
      g2.gain.linearRampToValueAtTime(0.1 * v.volumeMult, t + 0.005);
      g2.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
      g2.connect(dest);
      const o2 = ctx.createOscillator();
      o2.type = 'sawtooth';
      o2.frequency.setValueAtTime(180 * v.pitchMult, t);
      o2.connect(g2);
      o2.start(t);
      o2.stop(t + 0.09);
    },

    /** Ghost flutter — eerie tritone whisper with vibrato */
    butterfly_click(ctx, dest, v) {
      const t = ctx.currentTime + Math.max(0, v.delay);
      const notes = [466, 659]; // Bb4 → E5 (tritone)
      for (let i = 0; i < notes.length; i++) {
        const nt = t + i * 0.08;
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, nt);
        g.gain.linearRampToValueAtTime(0.15 * v.volumeMult, nt + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, nt + 0.25);
        g.connect(dest);
        const o = ctx.createOscillator();
        o.type = 'sine';
        o.frequency.setValueAtTime(notes[i] * v.pitchMult, nt);
        // Vibrato
        const vib = ctx.createOscillator();
        vib.type = 'sine';
        vib.frequency.value = 6;
        const vibG = ctx.createGain();
        vibG.gain.value = 15;
        vib.connect(vibG);
        vibG.connect(o.frequency);
        o.connect(g);
        o.start(nt);
        o.stop(nt + 0.26);
        vib.start(nt);
        vib.stop(nt + 0.26);
      }
    }
  }
};

// ══════════════════════════════════════════════════════════════════════
//  AMBIENT THEME DEFINITIONS
//  Each theme is a function (ctx, dest) => stopFn
//  Creates long-running procedural audio, returns cleanup function.
// ══════════════════════════════════════════════════════════════════════

/** Generate brown noise buffer (warm, low-frequency random noise) */
function _brownNoiseBuffer(ctx, seconds) {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  let last = 0;
  for (let i = 0; i < len; i++) {
    const white = Math.random() * 2 - 1;
    last = (last + 0.02 * white) / 1.02;
    d[i] = last * 3.5;
  }
  return buf;
}

/** Generate white noise buffer */
function _whiteNoiseBuffer(ctx, seconds) {
  const len = Math.floor(ctx.sampleRate * seconds);
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const d = buf.getChannelData(0);
  for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
  return buf;
}

// v1.5.3 §2 — renamed from AMBIENT_THEMES. Key lookup is now `currentMusic`
// (independent of SFX `currentTheme`). The sentinel key `none` provides a
// silent no-op generator so the existing startAmbient code path works
// without branching (player chooses "None" → no oscillators started).
// ─── House tuning: 432 Hz (A4 = 432) ────────────────────────────────────────
// Every generator below uses A4 = 432 Hz tuning rather than the 440 Hz
// concert standard. Note frequencies are precomputed in equal temperament
// against A4=432 (each track's comment lists the exact values it uses).
// Filter cutoffs, LFO rates, and bandpass centers are NOT retuned — they
// shape timbre and rhythm, not pitch. New tracks should follow the same
// convention so the whole score cross-mixes cleanly on shuffle.
// ────────────────────────────────────────────────────────────────────────────
const MUSIC_THEMES = {
  // ─── NONE — silent placeholder so "no background music" is a selection, ───
  // ─── not a separate branch. Returns a no-op stop function. ───────────────
  none(_ctx, _dest) {
    return () => { /* nothing to stop */ };
  },



  // ─── PASTORAL — 432 Hz generative ambient, wind, bird chirps ─────
  //
  // A minor pentatonic in 432 Hz tuning (A4=432):
  //   A3=216, C4=257.3, D4=288.7, E4=324, G4=385.3
  // Each note has its own slow LFO cycling it in/out at a different
  // rate, so the harmony evolves continuously and never repeats.
  //
  pastoral(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Soft wind (brown noise → LP 350 Hz)
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = _brownNoiseBuffer(ctx, 4);
    windSrc.loop = true;
    const windLP = ctx.createBiquadFilter();
    windLP.type = 'lowpass';
    windLP.frequency.value = 350;
    windLP.Q.value = 0.5;
    const windG = ctx.createGain();
    windG.gain.value = 0.06;
    windSrc.connect(windLP);
    windLP.connect(windG);
    windG.connect(dest);
    windSrc.start();
    nodes.push(windSrc);

    // Layer 2: 432 Hz generative pad — 5 pentatonic voices, each on its
    // own slow LFO so they bloom in and out independently.
    // Irrational-ish LFO rates ensure the pattern doesn't loop.
    const voices = [
      { freq: 216,   lfoRate: 0.037 },  // A3  — root drone, slowest cycle
      { freq: 257.3, lfoRate: 0.059 },  // C4  — minor third
      { freq: 288.7, lfoRate: 0.071 },  // D4  — fourth
      { freq: 324,   lfoRate: 0.047 },  // E4  — fifth
      { freq: 385.3, lfoRate: 0.083 },  // G4  — minor seventh
    ];

    for (const v of voices) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = v.freq;

      // Gain envelope driven by LFO: oscillates 0 → peak → 0
      const padG = ctx.createGain();
      padG.gain.value = 0.025;  // base level (DC offset for LFO)

      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      // Randomize LFO start phase by offsetting start time
      const phaseOffset = Math.random() * (1 / v.lfoRate);

      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.025;  // swings ±0.025 around the 0.025 base
      lfo.connect(lfoG);
      lfoG.connect(padG.gain);

      osc.connect(padG);
      padG.connect(dest);
      osc.start();
      lfo.start(ctx.currentTime + phaseOffset);
      nodes.push(osc, lfo);
    }

    // Layer 3: Gentle octave bass pulse (A2 = 108 Hz in 432 tuning)
    const bass = ctx.createOscillator();
    bass.type = 'sine';
    bass.frequency.value = 108;
    const bassG = ctx.createGain();
    bassG.gain.value = 0.02;
    const bassLfo = ctx.createOscillator();
    bassLfo.type = 'sine';
    bassLfo.frequency.value = 0.023;  // very slow swell
    const bassLfoG = ctx.createGain();
    bassLfoG.gain.value = 0.015;
    bassLfo.connect(bassLfoG);
    bassLfoG.connect(bassG.gain);
    bass.connect(bassG);
    bassG.connect(dest);
    bass.start();
    bassLfo.start();
    nodes.push(bass, bassLfo);

    // Layer 4: Random bird chirps (2-4 kHz sine blips, 5-18s apart)
    function scheduleChirp() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const freq = 2200 + Math.random() * 1800;
      const dur = 0.04 + Math.random() * 0.07;

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.035, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, t);
      o.frequency.linearRampToValueAtTime(freq * (0.85 + Math.random() * 0.3), t + dur);
      o.connect(g);
      o.start(t);
      o.stop(t + dur + 0.01);

      timers.push(setTimeout(scheduleChirp, 5000 + Math.random() * 13000));
    }
    timers.push(setTimeout(scheduleChirp, 3000 + Math.random() * 4000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── ARCADE — soft triangle hum, gentle square sub-octave ────────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  arcade(ctx, dest) {
    const nodes = [];

    // Layer 1: Quiet triangle wave + slow tremolo — A2 at 432 tuning
    const bass = ctx.createOscillator();
    bass.type = 'triangle';
    bass.frequency.value = 108;
    const bassG = ctx.createGain();
    bassG.gain.value = 0.055;
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.3;
    const lfoG = ctx.createGain();
    lfoG.gain.value = 0.025;
    lfo.connect(lfoG);
    lfoG.connect(bassG.gain);
    bass.connect(bassG);
    bassG.connect(dest);
    bass.start();
    lfo.start();
    nodes.push(bass, lfo);

    // Layer 2: Soft square sub-octave + gentle wobble — A1 at 432 tuning
    const sub = ctx.createOscillator();
    sub.type = 'square';
    sub.frequency.value = 54;
    const subG = ctx.createGain();
    subG.gain.value = 0.035;
    const subLfo = ctx.createOscillator();
    subLfo.type = 'sine';
    subLfo.frequency.value = 0.15;
    const subLfoG = ctx.createGain();
    subLfoG.gain.value = 0.01;
    subLfo.connect(subLfoG);
    subLfoG.connect(subG.gain);
    sub.connect(subG);
    subG.connect(dest);
    sub.start();
    subLfo.start();
    nodes.push(sub, subLfo);

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
    };
  },

  // ─── 8-BIT — low pulse drone + occasional quiet bleeps ──────────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  '8bit'(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Low pulse drone — A1 at 432 tuning
    const drone = ctx.createOscillator();
    drone.type = 'square';
    drone.frequency.value = 54;
    const droneG = ctx.createGain();
    droneG.gain.value = 0.04;
    drone.connect(droneG);
    droneG.connect(dest);
    drone.start();
    nodes.push(drone);

    // Layer 2: Random quiet bleeps — C major pentatonic in 432 tuning
    //   C4=256.87, E4=323.63, G4=384.87, C5=513.74, E5=647.26
    const bleepNotes = [256.87, 323.63, 384.87, 513.74, 647.26];
    function scheduleBleep() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const freq = bleepNotes[Math.floor(Math.random() * bleepNotes.length)];

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.05, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'square';
      o.frequency.value = freq;
      o.connect(g);
      o.start(t);
      o.stop(t + 0.09);

      timers.push(setTimeout(scheduleBleep, 4000 + Math.random() * 8000));
    }
    timers.push(setTimeout(scheduleBleep, 3000 + Math.random() * 5000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── LO-FI — tape hiss, warm detuned pad, vinyl crackle ─────────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  lofi(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Tape hiss (brown noise → LP 800 Hz)
    const hissSrc = ctx.createBufferSource();
    hissSrc.buffer = _brownNoiseBuffer(ctx, 4);
    hissSrc.loop = true;
    const hissLP = ctx.createBiquadFilter();
    hissLP.type = 'lowpass';
    hissLP.frequency.value = 800;
    const hissG = ctx.createGain();
    hissG.gain.value = 0.06;
    hissSrc.connect(hissLP);
    hissLP.connect(hissG);
    hissG.connect(dest);
    hissSrc.start();
    nodes.push(hissSrc);

    // Layer 2: Warm detuned pad — A3 at 432 tuning (216 + 217 = ~1 Hz beat)
    for (const freq of [216, 217]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.value = 0.045;
      osc.connect(g);
      g.connect(dest);
      osc.start();
      nodes.push(osc);
    }

    // Layer 3: Vinyl crackle (random HP-filtered noise impulses, 200ms-1s apart)
    function scheduleCrackle() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.003);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * 0.5;

      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 2000;
      const g = ctx.createGain();
      g.gain.value = 0.04 + Math.random() * 0.03;
      src.connect(hp);
      hp.connect(g);
      g.connect(dest);
      src.start(t);

      timers.push(setTimeout(scheduleCrackle, 200 + Math.random() * 800));
    }
    timers.push(setTimeout(scheduleCrackle, 500));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── SPOOKY — eerie wind, sub drone, occasional detuned tones ───
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  spooky(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Eerie wind (white noise → bandpass with sweeping center freq)
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = _whiteNoiseBuffer(ctx, 4);
    windSrc.loop = true;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 600;
    bp.Q.value = 3;
    const sweepLfo = ctx.createOscillator();
    sweepLfo.type = 'sine';
    sweepLfo.frequency.value = 0.08;
    const sweepG = ctx.createGain();
    sweepG.gain.value = 400;
    sweepLfo.connect(sweepG);
    sweepG.connect(bp.frequency);
    const windG = ctx.createGain();
    windG.gain.value = 0.07;
    windSrc.connect(bp);
    bp.connect(windG);
    windG.connect(dest);
    windSrc.start();
    sweepLfo.start();
    nodes.push(windSrc, sweepLfo);

    // Layer 2: Sub-bass drone with slow vibrato — A1 at 432 tuning (54 ±5 Hz)
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 54;
    const subVib = ctx.createOscillator();
    subVib.type = 'sine';
    subVib.frequency.value = 0.2;
    const subVibG = ctx.createGain();
    subVibG.gain.value = 5;
    subVib.connect(subVibG);
    subVibG.connect(sub.frequency);
    const subG = ctx.createGain();
    subG.gain.value = 0.05;
    sub.connect(subG);
    subG.connect(dest);
    sub.start();
    subVib.start();
    nodes.push(sub, subVib);

    // Layer 3: Random eerie tones — tritone/minor2 intervals in 432 tuning
    //   A3=216, Bb3=228.76, Eb4=305.35, F#4=363.27, Bb4=457.53
    const eerieFreqs = [216, 228.76, 305.35, 363.27, 457.53];
    function scheduleStab() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const freq = eerieFreqs[Math.floor(Math.random() * eerieFreqs.length)];
      const dur = 0.8 + Math.random() * 1.2;

      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.045, t + 0.3);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      g.connect(dest);

      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, t);
      o.frequency.linearRampToValueAtTime(freq * (0.97 + Math.random() * 0.06), t + dur);
      o.connect(g);
      o.start(t);
      o.stop(t + dur + 0.01);

      timers.push(setTimeout(scheduleStab, 8000 + Math.random() * 15000));
    }
    timers.push(setTimeout(scheduleStab, 3000 + Math.random() * 5000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── MEADOW DAWN — warm sunrise, C major, slow arpeggio, bird chirps ────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  meadow_dawn(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Sustained C major pad (C3, E3, G3) in 432 tuning
    const voices = [
      { freq: 128.43, lfoRate: 0.041 }, // C3
      { freq: 161.83, lfoRate: 0.053 }, // E3
      { freq: 192.44, lfoRate: 0.067 }, // G3
    ];
    for (const v of voices) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = v.freq;
      const g = ctx.createGain();
      g.gain.value = 0.035;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.025;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(dest);
      osc.start();
      lfo.start(ctx.currentTime + Math.random() * 5);
      nodes.push(osc, lfo);
    }

    // Layer 2: Ascending C major arpeggio (C5 E5 G5 C6) in 432 tuning
    const arpNotes = [513.74, 647.26, 769.74, 1027.47];
    function scheduleArp() {
      if (!ctx || ctx.state === 'closed') return;
      let t = ctx.currentTime;
      for (const f of arpNotes) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.055, t + 0.02);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
        g.connect(dest);
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = f;
        o.connect(g);
        o.start(t);
        o.stop(t + 0.55);
        t += 0.35;
      }
      timers.push(setTimeout(scheduleArp, 6000 + Math.random() * 7000));
    }
    timers.push(setTimeout(scheduleArp, 3000 + Math.random() * 3000));

    // Layer 3: Occasional bird chirps (same recipe as pastoral)
    function scheduleChirp() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const freq = 2400 + Math.random() * 2000;
      const dur = 0.05 + Math.random() * 0.06;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.04, t + 0.008);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.setValueAtTime(freq, t);
      o.frequency.linearRampToValueAtTime(freq * (0.85 + Math.random() * 0.3), t + dur);
      o.connect(g);
      o.start(t);
      o.stop(t + dur + 0.01);
      timers.push(setTimeout(scheduleChirp, 6000 + Math.random() * 12000));
    }
    timers.push(setTimeout(scheduleChirp, 2000 + Math.random() * 4000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── MIDNIGHT COOP — sparse, quiet; A minor long sustains, heavy LP ──
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  midnight_coop(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Low A minor drone (A2, C3, E3) in 432 tuning, through a warm LP.
    // LP cutoff lifted from 400 Hz → 1100 Hz so the chord actually reads;
    // each voice gets its own slow LFO so the drone breathes instead of sitting.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1100;
    lp.Q.value = 0.7;
    lp.connect(dest);
    const coopVoices = [
      { freq: 108,    lfoRate: 0.039 },
      { freq: 128.43, lfoRate: 0.057 },
      { freq: 161.83, lfoRate: 0.071 },
    ];
    for (const v of coopVoices) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = v.freq;
      const g = ctx.createGain();
      g.gain.value = 0.04;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.025;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(lp);
      osc.start();
      lfo.start(ctx.currentTime + Math.random() * 5);
      nodes.push(osc, lfo);
    }

    // Layer 2: Very slow breath-like sub swell — A1 at 432 tuning
    const sub = ctx.createOscillator();
    sub.type = 'sine';
    sub.frequency.value = 54;
    const subG = ctx.createGain();
    subG.gain.value = 0.03;
    const subLfo = ctx.createOscillator();
    subLfo.type = 'sine';
    subLfo.frequency.value = 0.05;
    const subLfoG = ctx.createGain();
    subLfoG.gain.value = 0.022;
    subLfo.connect(subLfoG);
    subLfoG.connect(subG.gain);
    sub.connect(subG);
    subG.connect(dest);
    sub.start();
    subLfo.start();
    nodes.push(sub, subLfo);

    // Layer 3: Rare high whisper notes — A minor pentatonic in 432 tuning
    //   A4=432, C5=513.74, E5=647.26, G5=769.74
    const whispers = [432, 513.74, 647.26, 769.74];
    function scheduleWhisper() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const freq = whispers[Math.floor(Math.random() * whispers.length)];
      const dur = 2 + Math.random() * 2;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.03, t + 0.8);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = freq;
      o.connect(g);
      o.start(t);
      o.stop(t + dur + 0.05);
      timers.push(setTimeout(scheduleWhisper, 10000 + Math.random() * 10000));
    }
    timers.push(setTimeout(scheduleWhisper, 8000 + Math.random() * 10000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── DUST BATH — playful; G major pentatonic plucks + light percussion ──
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  dust_bath(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Light rhythmic G pulse — G2 at 432 tuning
    const pulse = ctx.createOscillator();
    pulse.type = 'triangle';
    pulse.frequency.value = 96.22; // G2 in 432
    const pulseG = ctx.createGain();
    pulseG.gain.value = 0.04;
    pulse.connect(pulseG);
    pulseG.connect(dest);
    pulse.start();
    nodes.push(pulse);

    // Layer 2: G major pentatonic plucks (G, A, B, D, E) in 432 tuning
    //   G4=384.87, A4=432, B4=484.99, D5=576.84, E5=647.26
    const pentatonic = [384.87, 432, 484.99, 576.84, 647.26];
    function schedulePluck() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const f = pentatonic[Math.floor(Math.random() * pentatonic.length)];
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.05, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      o.connect(g);
      o.start(t);
      o.stop(t + 0.3);
      timers.push(setTimeout(schedulePluck, 600 + Math.random() * 800));
    }
    timers.push(setTimeout(schedulePluck, 400 + Math.random() * 500));

    // Layer 3: Light brush percussion (short HP-filtered noise at random beats)
    function scheduleBrush() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.04);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * (1 - i / len);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 3000;
      const g = ctx.createGain();
      g.gain.value = 0.04;
      src.connect(hp);
      hp.connect(g);
      g.connect(dest);
      src.start(t);
      timers.push(setTimeout(scheduleBrush, 1500 + Math.random() * 2000));
    }
    timers.push(setTimeout(scheduleBrush, 800 + Math.random() * 1000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── STORM BREWING — tense; D minor drones + distant thunder ──────────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  storm_brewing(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Distant wind (brown noise, LP 250 Hz)
    const windSrc = ctx.createBufferSource();
    windSrc.buffer = _brownNoiseBuffer(ctx, 4);
    windSrc.loop = true;
    const windLP = ctx.createBiquadFilter();
    windLP.type = 'lowpass';
    windLP.frequency.value = 250;
    const windG = ctx.createGain();
    windG.gain.value = 0.08;
    windSrc.connect(windLP);
    windLP.connect(windG);
    windG.connect(dest);
    windSrc.start();
    nodes.push(windSrc);

    // Layer 2: D minor modal drone (D, F, A) in 432 tuning, slight detune
    //   D3=144.16, F3=171.44, A3=216
    for (const f of [144.16, 171.44, 216]) {
      for (const offset of [-0.5, 0.5]) {
        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = f + offset;
        const g = ctx.createGain();
        g.gain.value = 0.02;
        osc.connect(g);
        g.connect(dest);
        osc.start();
        nodes.push(osc);
      }
    }

    // Layer 3: Distant thunder — low-passed noise burst with slow attack
    function scheduleThunder() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 2.5);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1);
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const lp = ctx.createBiquadFilter();
      lp.type = 'lowpass';
      lp.frequency.value = 180;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.09, t + 0.8);
      g.gain.exponentialRampToValueAtTime(0.001, t + 2.5);
      src.connect(lp);
      lp.connect(g);
      g.connect(dest);
      src.start(t);
      timers.push(setTimeout(scheduleThunder, 20000 + Math.random() * 25000));
    }
    timers.push(setTimeout(scheduleThunder, 12000 + Math.random() * 10000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── SUMMER AFTERNOON — lazy; F lydian sine pad + cicada rustles ──────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  summer_afternoon(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: F lydian pad (F, A, B, C, E) in 432 tuning — each voice slow LFO
    const voices = [
      { freq: 171.44, lfoRate: 0.031 }, // F3
      { freq: 216,    lfoRate: 0.047 }, // A3
      { freq: 242.45, lfoRate: 0.061 }, // B3 (the #4 — lydian)
      { freq: 256.87, lfoRate: 0.053 }, // C4
      { freq: 323.67, lfoRate: 0.071 }, // E4
    ];
    for (const v of voices) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = v.freq;
      const g = ctx.createGain();
      g.gain.value = 0.032;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.025;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(dest);
      osc.start();
      lfo.start(ctx.currentTime + Math.random() * 8);
      nodes.push(osc, lfo);
    }

    // Layer 2: Cicada rustles — bandpass noise ticks at irregular intervals
    function scheduleCicada() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.4);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) {
        const env = Math.exp(-i / (len * 0.4));
        d[i] = (Math.random() * 2 - 1) * env * (0.5 + 0.5 * Math.sin(i * 0.08));
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 4500 + Math.random() * 2000;
      bp.Q.value = 6;
      const g = ctx.createGain();
      g.gain.value = 0.04;
      src.connect(bp);
      bp.connect(g);
      g.connect(dest);
      src.start(t);
      timers.push(setTimeout(scheduleCicada, 3000 + Math.random() * 6000));
    }
    timers.push(setTimeout(scheduleCicada, 2000 + Math.random() * 3000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── HARVEST MOON — autumnal; D dorian + brass-like synth + paper texture ─
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  harvest_moon(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: D dorian pad (D, F, G, A, C) in 432 tuning — saw + LP for warmth.
    // LP cutoff lifted 700 → 1400 Hz so the saws keep their body; gains
    // doubled because the old LP was gutting them to inaudibility.
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 1400;
    lp.Q.value = 1.2;
    lp.connect(dest);
    const voices = [
      { freq: 144.16, lfoRate: 0.037 }, // D3
      { freq: 171.44, lfoRate: 0.051 }, // F3
      { freq: 192.44, lfoRate: 0.061 }, // G3
      { freq: 216,    lfoRate: 0.047 }, // A3
      { freq: 256.87, lfoRate: 0.073 }, // C4
    ];
    for (const v of voices) {
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.value = v.freq;
      const g = ctx.createGain();
      g.gain.value = 0.025;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.02;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(lp);
      osc.start();
      lfo.start(ctx.currentTime + Math.random() * 5);
      nodes.push(osc, lfo);
    }

    // Layer 2: Paper rustles — filtered brown-noise bursts at slow cadence
    function scheduleRustle() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.6);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      let last = 0;
      for (let i = 0; i < len; i++) {
        const white = Math.random() * 2 - 1;
        last = (last + 0.02 * white) / 1.02;
        d[i] = last * Math.exp(-i / (len * 0.5));
      }
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 1200;
      const g = ctx.createGain();
      g.gain.value = 0.035;
      src.connect(hp);
      hp.connect(g);
      g.connect(dest);
      src.start(t);
      timers.push(setTimeout(scheduleRustle, 4000 + Math.random() * 5000));
    }
    timers.push(setTimeout(scheduleRustle, 3000 + Math.random() * 3000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── BARN DANCE — upbeat; G major triadic hits at ~90 BPM shuffle ──────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  barn_dance(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Low G bass — steady pulse at 90 BPM (~667 ms/beat)
    function scheduleBass() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.05, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = 96.22; // G2 in 432
      o.connect(g);
      o.start(t);
      o.stop(t + 0.3);
      timers.push(setTimeout(scheduleBass, 667));
    }
    timers.push(setTimeout(scheduleBass, 500));

    // Layer 2: G major broken chord arpeggio (G B D G) in 432 tuning
    //   G3=192.44, B3=242.45, D4=288.32, G4=384.87
    const arp = [192.44, 242.45, 288.32, 384.87];
    function scheduleArp() {
      if (!ctx || ctx.state === 'closed') return;
      let t = ctx.currentTime;
      for (const f of arp) {
        const g = ctx.createGain();
        g.gain.setValueAtTime(0.001, t);
        g.gain.linearRampToValueAtTime(0.04, t + 0.01);
        g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
        g.connect(dest);
        const o = ctx.createOscillator();
        o.type = 'triangle';
        o.frequency.value = f;
        o.connect(g);
        o.start(t);
        o.stop(t + 0.35);
        t += 0.333;
      }
      timers.push(setTimeout(scheduleArp, 2667 + Math.random() * 1000));
    }
    timers.push(setTimeout(scheduleArp, 1000 + Math.random() * 500));

    // Layer 3: Handclap-like percussion on beats 2 and 4
    function scheduleClap() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.06);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.exp(-i / (len * 0.3));
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const bp = ctx.createBiquadFilter();
      bp.type = 'bandpass';
      bp.frequency.value = 1500;
      bp.Q.value = 2;
      const g = ctx.createGain();
      g.gain.value = 0.04;
      src.connect(bp);
      bp.connect(g);
      g.connect(dest);
      src.start(t);
      timers.push(setTimeout(scheduleClap, 1333));
    }
    timers.push(setTimeout(scheduleClap, 1000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── FIRST FROST — crystalline; E major bell-like FM synth ─────────────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  first_frost(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: E major sustained pad (E, G#, B) in 432 tuning — slow drift
    //   E3=161.83, G#3=203.87, B3=242.45
    for (const v of [{ freq: 161.83, lfoRate: 0.029 }, { freq: 203.87, lfoRate: 0.043 }, { freq: 242.45, lfoRate: 0.059 }]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = v.freq;
      const g = ctx.createGain();
      g.gain.value = 0.032;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.022;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(dest);
      osc.start();
      lfo.start(ctx.currentTime + Math.random() * 5);
      nodes.push(osc, lfo);
    }

    // Layer 2: Bell-like FM hits — carrier sine modulated by a second sine,
    // shimmery decay. E major tones (E5 G#5 B5 E6) in 432 tuning.
    //   E5=647.26, G#5=815.49, B5=969.82, E6=1294.53
    const bells = [647.26, 815.49, 969.82, 1294.53];
    function scheduleBell() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const f = bells[Math.floor(Math.random() * bells.length)];
      const carrier = ctx.createOscillator();
      carrier.type = 'sine';
      carrier.frequency.value = f;
      const mod = ctx.createOscillator();
      mod.type = 'sine';
      mod.frequency.value = f * 3.1;
      const modG = ctx.createGain();
      modG.gain.value = 200;
      mod.connect(modG);
      modG.connect(carrier.frequency);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.06, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.001, t + 2.5);
      carrier.connect(g);
      g.connect(dest);
      carrier.start(t);
      mod.start(t);
      carrier.stop(t + 2.6);
      mod.stop(t + 2.6);
      timers.push(setTimeout(scheduleBell, 3000 + Math.random() * 4000));
    }
    timers.push(setTimeout(scheduleBell, 2000 + Math.random() * 3000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── DISTANT FIELDS — reflective; B minor pentatonic slow arp + pad ────
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  distant_fields(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: B minor pad (B, D, F#) in 432 tuning
    //   B2=121.22, D3=144.16, F#3=181.63
    for (const v of [{ freq: 121.22, lfoRate: 0.033 }, { freq: 144.16, lfoRate: 0.049 }, { freq: 181.63, lfoRate: 0.063 }]) {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = v.freq;
      const g = ctx.createGain();
      g.gain.value = 0.028;
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = v.lfoRate;
      const lfoG = ctx.createGain();
      lfoG.gain.value = 0.018;
      lfo.connect(lfoG);
      lfoG.connect(g.gain);
      osc.connect(g);
      g.connect(dest);
      osc.start();
      lfo.start(ctx.currentTime + Math.random() * 6);
      nodes.push(osc, lfo);
    }

    // Layer 2: Slow arp up-down B minor pentatonic (B, D, E, F#, A) in 432
    //   B3=242.45, D4=288.32, E4=323.67, F#4=363.26, A4=432
    const arp = [242.45, 288.32, 323.67, 363.26, 432];
    let dir = 1;
    let idx = 0;
    function scheduleStep() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const f = arp[idx];
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.042, t + 0.05);
      g.gain.exponentialRampToValueAtTime(0.001, t + 1.2);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = f;
      o.connect(g);
      o.start(t);
      o.stop(t + 1.3);
      idx += dir;
      if (idx >= arp.length - 1) dir = -1;
      else if (idx <= 0) dir = 1;
      timers.push(setTimeout(scheduleStep, 1400 + Math.random() * 400));
    }
    timers.push(setTimeout(scheduleStep, 2000));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  },

  // ─── HEARTHSIDE — cozy indoor; A major plucks + low hum + fire crackle ──
  // All notes tuned to 432 Hz (A4=432), matching pastoral.
  hearthside(ctx, dest) {
    const nodes = [];
    const timers = [];

    // Layer 1: Low hum (brown noise, LP 180 Hz)
    const humSrc = ctx.createBufferSource();
    humSrc.buffer = _brownNoiseBuffer(ctx, 4);
    humSrc.loop = true;
    const humLP = ctx.createBiquadFilter();
    humLP.type = 'lowpass';
    humLP.frequency.value = 180;
    const humG = ctx.createGain();
    humG.gain.value = 0.06;
    humSrc.connect(humLP);
    humLP.connect(humG);
    humG.connect(dest);
    humSrc.start();
    nodes.push(humSrc);

    // Layer 2: A major plucks (A, C#, E) in 432 tuning — triangle, fast decay
    //   A3=216, C#4=272.16, E4=323.67, A4=432, C#5=544.35
    const plucks = [216, 272.16, 323.67, 432, 544.35];
    function schedulePluck() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const f = plucks[Math.floor(Math.random() * plucks.length)];
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.001, t);
      g.gain.linearRampToValueAtTime(0.045, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.8);
      g.connect(dest);
      const o = ctx.createOscillator();
      o.type = 'triangle';
      o.frequency.value = f;
      o.connect(g);
      o.start(t);
      o.stop(t + 0.85);
      timers.push(setTimeout(schedulePluck, 2500 + Math.random() * 3000));
    }
    timers.push(setTimeout(schedulePluck, 1500 + Math.random() * 2000));

    // Layer 3: Fire crackle — random HP noise impulses (sparser than lofi)
    function scheduleCrackle() {
      if (!ctx || ctx.state === 'closed') return;
      const t = ctx.currentTime;
      const len = Math.floor(ctx.sampleRate * 0.008);
      const buf = ctx.createBuffer(1, len, ctx.sampleRate);
      const d = buf.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * 0.6;
      const src = ctx.createBufferSource();
      src.buffer = buf;
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 2500;
      const g = ctx.createGain();
      g.gain.value = 0.05 + Math.random() * 0.04;
      src.connect(hp);
      hp.connect(g);
      g.connect(dest);
      src.start(t);
      timers.push(setTimeout(scheduleCrackle, 400 + Math.random() * 1200));
    }
    timers.push(setTimeout(scheduleCrackle, 600));

    return () => {
      for (const n of nodes) { try { n.stop(); } catch (e) { /* already stopped */ } }
      for (const t of timers) clearTimeout(t);
    };
  }
};

// v1.5.3 §2 — Display labels for MUSIC_THEMES keys. Keep in sync with
// MUSIC_THEMES. UI code (index.html settings, dev.html preview) builds
// option lists from getAvailableMusic() then renders with this label map
// so we have one source of truth for track naming. Missing labels fall
// back to the key itself.
const MUSIC_LABELS = {
  none: 'None (silent)',
  pastoral: 'Pastoral',
  arcade: 'Arcade',
  '8bit': '8-Bit',
  lofi: 'Lo-Fi',
  spooky: 'Spooky',
  meadow_dawn: 'Meadow Dawn',
  midnight_coop: 'Midnight Coop',
  dust_bath: 'Dust Bath',
  storm_brewing: 'Storm Brewing',
  summer_afternoon: 'Summer Afternoon',
  harvest_moon: 'Harvest Moon',
  barn_dance: 'Barn Dance',
  first_frost: 'First Frost',
  distant_fields: 'Distant Fields',
  hearthside: 'Hearthside',
};

// v1.5.3 §2 — UI ordering: Pastoral pinned first (anchor for familiarity),
// alphabetical in the middle, None at the end (a deliberate opt-out).
// Pure helper for tests + UI consumers.
export function getOrderedMusicKeys() {
  const all = Object.keys(MUSIC_THEMES);
  const middle = all
    .filter(k => k !== 'pastoral' && k !== 'none')
    .sort((a, b) => (MUSIC_LABELS[a] || a).localeCompare(MUSIC_LABELS[b] || b));
  const ordered = [];
  if (all.includes('pastoral')) ordered.push('pastoral');
  ordered.push(...middle);
  if (all.includes('none')) ordered.push('none');
  return ordered;
}

export function getMusicLabel(key) {
  return MUSIC_LABELS[key] || key;
}

// ══════════════════════════════════════════════════════════════════════
//  AUDIO MANAGER
// ══════════════════════════════════════════════════════════════════════

const audioManager = {

  /** Create AudioContext and wire the bus graph. Idempotent. */
  init() {
    if (audioCtx) return;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      console.warn('Web Audio API not available:', e.message);
      return;
    }

    // Compressor → destination
    compressor = audioCtx.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-24, audioCtx.currentTime);
    compressor.knee.setValueAtTime(30, audioCtx.currentTime);
    compressor.ratio.setValueAtTime(4, audioCtx.currentTime);
    compressor.attack.setValueAtTime(0.003, audioCtx.currentTime);
    compressor.release.setValueAtTime(0.25, audioCtx.currentTime);
    compressor.connect(audioCtx.destination);

    // Master gain → compressor
    masterGain = audioCtx.createGain();
    masterGain.connect(compressor);

    // SFX bus → master
    sfxBus = audioCtx.createGain();
    sfxBus.connect(masterGain);

    // Ambient bus → master
    ambientBus = audioCtx.createGain();
    ambientBus.connect(masterGain);

    // Music bus → master (reserved for future)
    musicBus = audioCtx.createGain();
    musicBus.connect(masterGain);

    // Restore persisted settings
    currentTheme = _loadPref('henhouse:v1:audio:sfxTheme', 'pastoral');
    // v1.5.3 §2 back-compat — existing players have `henHouseSoundTheme` set
    // but no `henHouseMusicTheme`. Fall back to the sound theme key so a
    // pastoral-and-pastoral player sees no change; explicit music selection
    // after upgrade persists and overrides this fallback.
    currentMusic = _loadPref('henhouse:v1:audio:music', currentTheme);
    // v1.5.4 §1 — default mode is 'random' for new players. Existing save
    // keys default to 'random' too so the first post-upgrade session gets
    // the expanded-track experience; players who dislike it can switch to
    // 'specific' in the Settings tab.
    _musicMode = _loadPref('henhouse:v1:audio:musicMode', 'random');
    _masterVol = parseFloat(_loadPref('henhouse:v1:audio:master', '0.7'));
    _sfxVol = parseFloat(_loadPref('henhouse:v1:audio:sfx', '0.8'));
    _ambientVol = parseFloat(_loadPref('henhouse:v1:audio:ambient', '0.5'));

    masterGain.gain.setValueAtTime(_masterVol, audioCtx.currentTime);
    sfxBus.gain.setValueAtTime(_sfxVol, audioCtx.currentTime);
    ambientBus.gain.setValueAtTime(_ambientVol, audioCtx.currentTime);
    musicBus.gain.setValueAtTime(0, audioCtx.currentTime); // silent until content exists
  },

  /** Load sample buffers (Phase 3 — currently no-op). */
  async load() {
    if (!audioCtx || loaded) return;
    loaded = true;
    // All sounds are procedural synth — no sample files to load
  },

  /**
   * Play a sound by ID using the current theme's synth generator.
   * Applies variation, respects rate limit and frame cap.
   * @param {string} soundId - e.g. 'egg_click', 'golden_egg'
   * @param {object} [opts] - optional { crowPatch } to shape the rooster_crow synth
   */
  play(soundId, opts) {
    if (!enabled || !audioCtx) return;

    // Resume suspended context (Chrome autoplay policy)
    if (audioCtx.state === 'suspended') {
      audioCtx.resume();
    }

    // Rate limit: 100ms cooldown per sound ID
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now();
    if (lastPlayTime[soundId] && now - lastPlayTime[soundId] < RATE_LIMIT_MS) return;

    // Frame limit: max 3 sounds per ~16ms
    if (now - _frameTime > 16) {
      _frameSoundCount = 0;
      _frameTime = now;
    }
    if (_frameSoundCount >= MAX_SOUNDS_PER_FRAME) return;

    // Look up synth generator in current theme
    const theme = SOUND_THEMES[currentTheme];
    if (!theme || typeof theme[soundId] !== 'function') return;

    const v = getVariation();
    if (opts && opts.crowPatch) applyCrowPatch(v, opts.crowPatch);
    try {
      theme[soundId](audioCtx, sfxBus, v);
      lastPlayTime[soundId] = now;
      _frameSoundCount++;
    } catch (e) {
      // Silently fail — audio is optional
    }
  },

  /**
   * Play a breed-specific rooster crow. Looks up CROW_PATCHES[breedId] and
   * packs the patch into the variation object before invoking the current
   * theme's rooster_crow synth. Falls back to DEFAULT (RIR) if the breed
   * is unknown. See CROW_PATCHES above for the per-breed axes.
   * @param {string} breedId - e.g. 'RIR', 'SULTAN', 'BRAHMA'
   */
  playCrow(breedId) {
    const patch = CROW_PATCHES[breedId] || CROW_PATCHES.DEFAULT;
    this.play('rooster_crow', { crowPatch: patch });
  },

  /**
   * Play a breed-specific rooster cluck — the tidbitting/food-call sound,
   * distinct from the cock-a-doodle-doo crow. Reuses CROW_PATCHES for
   * per-breed pitch/bright/env shaping. The synth itself rolls one of
   * three rhythm variants per call (tidbit / soft coo / alert) so a
   * single rooster has three distinct clucks that rotate.
   * @param {string} breedId - e.g. 'RIR', 'COCHIN', 'SERAMA'
   */
  playRoosterCluck(breedId) {
    const patch = CROW_PATCHES[breedId] || CROW_PATCHES.DEFAULT;
    this.play('rooster_cluck', { crowPatch: patch });
  },

  /**
   * Play a breed-specific hen cluck. Each breed has its own pitch/bright/
   * env/syllable bias via HEN_PATCHES so a Brahma clucks low-and-slow
   * while a Serama peeps high-and-chatty. Patch fields share the same
   * shape as CROW_PATCHES so the same applyCrowPatch pipeline is reused.
   * @param {string} breedId - e.g. 'RIR', 'SILKIE', 'BRAHMA'
   */
  playHenCluck(breedId) {
    const patch = HEN_PATCHES[breedId] || HEN_PATCHES.DEFAULT;
    this.play('hen_cluck', { crowPatch: patch });
  },

  /**
   * Enable or disable all audio output.
   * On first enable, auto-initializes the AudioContext (requires user gesture).
   */
  setEnabled(val) {
    enabled = val;
    if (val) {
      if (!audioCtx) {
        this.init();
        this.load();
      }
      if (audioCtx && audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
      // v1.5.4 §1 — in random mode, pick a fresh track every time sound
      // is enabled, and start the 5-min re-shuffle timer. Specific mode
      // keeps the saved track and skips the timer.
      if (_musicMode === 'random') {
        this.shuffleMusic();
        this._startShuffleTimer();
      }
      // Auto-start ambient if volume > 0
      if (_ambientVol > 0 && !_ambientStopFn) {
        this.startAmbient();
      }
    } else {
      this.stopAmbient();
      this._stopShuffleTimer();
    }
  },

  isEnabled() {
    return enabled;
  },

  // ── Theme switching ─────────────────────────────────────────────

  // v1.5.3 §2 — setTheme now controls SFX only. Ambient/music bed is
  // controlled separately via setMusic. Pre-v1.5.3 behavior of "picking a
  // sound theme also swapped ambient" is intentionally dropped — players
  // who want the bundled feel can pick matching MUSIC_THEMES.
  setTheme(name) {
    if (!SOUND_THEMES[name]) return;
    currentTheme = name;
    _savePref('henhouse:v1:audio:sfxTheme', name);
  },

  getTheme() {
    return currentTheme;
  },

  getThemeNames() {
    return Object.keys(SOUND_THEMES);
  },

  // v1.5.3 §2 — independent background-music selector. Crossfades the
  // ambient bed to the new generator if one was already playing, same
  // pattern as the pre-split setTheme crossfade.
  // v1.5.4 — setMusic is the plumbing call; it doesn't change mode. UI
  // code that lets the player pin a track should call setMusicMode first
  // (or use pickMusic which bundles both).
  setMusic(name) {
    if (!MUSIC_THEMES[name]) return;
    const wasPlaying = !!_ambientStopFn;
    currentMusic = name;
    _savePref('henhouse:v1:audio:music', name);
    if (wasPlaying) {
      this.stopAmbient();
      this.startAmbient();
    }
  },

  getMusic() {
    return currentMusic;
  },

  getAvailableMusic() {
    return Object.keys(MUSIC_THEMES);
  },

  // v1.5.4 §1 — Mode + shuffle API.
  //
  // Flow:
  //   random:   on enable, pick a track; every 5 min pick a new one.
  //   specific: pin to currentMusic; shuffle is disabled.
  //
  // pickMusic(key) is the "user picked a specific track" entry point —
  // flips mode to 'specific' and sets the track in one call.
  getMusicMode() { return _musicMode; },

  setMusicMode(mode) {
    if (mode !== 'random' && mode !== 'specific') return;
    _musicMode = mode;
    _savePref('henhouse:v1:audio:musicMode', mode);
    if (mode === 'random') {
      this._startShuffleTimer();
    } else {
      this._stopShuffleTimer();
    }
  },

  pickMusic(key) {
    if (!MUSIC_THEMES[key]) return;
    this.setMusicMode('specific');
    this.setMusic(key);
  },

  // Pick a random playable track (excludes the silent `none` sentinel)
  // and route through setMusic so the crossfade runs if ambient is live.
  shuffleMusic() {
    const keys = Object.keys(MUSIC_THEMES).filter(k => k !== 'none');
    if (keys.length === 0) return;
    // Avoid re-picking the same track twice in a row where possible
    let pick = keys[Math.floor(Math.random() * keys.length)];
    if (pick === currentMusic && keys.length > 1) {
      const others = keys.filter(k => k !== currentMusic);
      pick = others[Math.floor(Math.random() * others.length)];
    }
    this.setMusic(pick);
  },

  _startShuffleTimer() {
    if (_shuffleTimer) return;
    _shuffleTimer = setInterval(() => {
      if (_musicMode !== 'random') return;
      if (!enabled || _ambientVol <= 0) return;
      this.shuffleMusic();
    }, SHUFFLE_INTERVAL_MS);
    if (_shuffleTimer && typeof _shuffleTimer.unref === 'function') {
      _shuffleTimer.unref();
    }
  },

  _stopShuffleTimer() {
    if (!_shuffleTimer) return;
    clearInterval(_shuffleTimer);
    _shuffleTimer = null;
  },

  // ── Volume controls ─────────────────────────────────────────────

  setMasterVolume(v) {
    _masterVol = Math.max(0, Math.min(1, v));
    if (masterGain && audioCtx) {
      masterGain.gain.setValueAtTime(_masterVol, audioCtx.currentTime);
    }
    _savePref('henhouse:v1:audio:master', _masterVol.toString());
  },

  getMasterVolume() {
    return _masterVol;
  },

  setSfxVolume(v) {
    _sfxVol = Math.max(0, Math.min(1, v));
    if (sfxBus && audioCtx) {
      sfxBus.gain.setValueAtTime(_sfxVol, audioCtx.currentTime);
    }
    _savePref('henhouse:v1:audio:sfx', _sfxVol.toString());
  },

  getSfxVolume() {
    return _sfxVol;
  },

  setAmbientVolume(v) {
    _ambientVol = Math.max(0, Math.min(1, v));
    if (ambientBus && audioCtx) {
      ambientBus.gain.setValueAtTime(_ambientVol, audioCtx.currentTime);
    }
    _savePref('henhouse:v1:audio:ambient', _ambientVol.toString());
    // Start ambient if volume went from 0 to >0 while enabled
    if (enabled && _ambientVol > 0 && !_ambientStopFn) {
      this.startAmbient();
    }
    // Stop ambient if volume went to 0
    if (_ambientVol === 0 && _ambientStopFn) {
      this.stopAmbient();
    }
  },

  getAmbientVolume() {
    return _ambientVol;
  },

  // ── Ambient loop control ────────────────────────────────────────

  /** Start the ambient loop for the current theme. No-op if already playing. */
  startAmbient() {
    if (_ambientStopFn || !audioCtx || !enabled) return;
    if (audioCtx.state === 'suspended') audioCtx.resume();

    // v1.5.3 §2 — ambient is driven by `currentMusic` now, not `currentTheme`.
    // The MUSIC_THEMES.none generator is a silent no-op, so "None" is a
    // selection (not a separate branch) and the fade/bus wiring still runs.
    const gen = MUSIC_THEMES[currentMusic];
    if (!gen) return;

    // Create a fade gain node between ambient generator and ambientBus
    _ambientFadeGain = audioCtx.createGain();
    _ambientFadeGain.gain.setValueAtTime(0.001, audioCtx.currentTime);
    _ambientFadeGain.gain.linearRampToValueAtTime(1, audioCtx.currentTime + 1);
    _ambientFadeGain.connect(ambientBus);

    try {
      _ambientStopFn = gen(audioCtx, _ambientFadeGain);
    } catch (e) {
      _ambientStopFn = null;
      _ambientFadeGain = null;
    }
  },

  /** Stop the ambient loop with a 1s fade-out. */
  stopAmbient() {
    if (!_ambientStopFn) return;
    const stopFn = _ambientStopFn;
    const fadeGain = _ambientFadeGain;
    _ambientStopFn = null;
    _ambientFadeGain = null;

    if (fadeGain && audioCtx) {
      // Fade out over 1s, then clean up
      fadeGain.gain.setValueAtTime(fadeGain.gain.value, audioCtx.currentTime);
      fadeGain.gain.linearRampToValueAtTime(0.001, audioCtx.currentTime + 1);
      setTimeout(() => {
        try { stopFn(); } catch (e) { /* already stopped */ }
        try { fadeGain.disconnect(); } catch (e) { /* ok */ }
      }, 1100);
    } else {
      try { stopFn(); } catch (e) { /* already stopped */ }
    }
  },

  /** @returns {boolean} Whether ambient is currently playing */
  isAmbientPlaying() {
    return !!_ambientStopFn;
  },

  // ── Internals exposed for dev soundboard ────────────────────────

  /** @returns {AudioContext|null} The raw AudioContext */
  getContext() { return audioCtx; },

  /** @returns {GainNode|null} The SFX bus node */
  getSfxBus() { return sfxBus; },

  /** @returns {GainNode|null} The ambient bus node */
  getAmbientBus() { return ambientBus; }
};

// ── Visibility API: fade ambient/music when tab hidden ──────────────

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (!audioCtx || !enabled) return;

    if (document.hidden) {
      // Ambient keeps playing in background (user knows game is running)
      // Only fade music bus (future) on hide
      _musicBeforeHide = 0;
      if (musicBus) {
        musicBus.gain.linearRampToValueAtTime(0, audioCtx.currentTime + 0.2);
      }
    } else {
      // Fade music back in on return
      if (musicBus) {
        musicBus.gain.linearRampToValueAtTime(_musicBeforeHide, audioCtx.currentTime + 0.5);
      }
      // Resume context if it was suspended by the browser
      if (audioCtx.state === 'suspended') {
        audioCtx.resume();
      }
    }
  });
}

// ── Exports ─────────────────────────────────────────────────────────

export default audioManager;
export { SOUND_THEMES, MUSIC_THEMES, getVariation, RATE_LIMIT_MS, SOUND_PRIORITY, MAX_SOUNDS_PER_FRAME };
